if (window._ddRegOverlayInit) throw new Error('duplicate');
window._ddRegOverlayInit = true;

/* ═══════════════════════════════════════════════════════════════════
 * OVERLAY DIAGNOSTIC INSTRUMENTATION (opt-in)
 *
 * `_dbg(event, data)` writes a state-event into localStorage so a trace
 * can survive top-level navigations (the auto-login form POST tears
 * down the document, which is where most "iframe closed and didn't
 * auto log me in" reports got lost in normal console logs).
 *
 * GATED OFF BY DEFAULT. Enable in either way:
 *   • Append `?ddregdebug=1` to the page URL, or
 *   • Run `localStorage.ddRegOverlayDebug = '1'` once in the console.
 *
 * Inspect (works regardless of gate state — operates on whatever was
 * captured while the gate was open):
 *   window.__regOverlayDebug.print()   // pretty table
 *   window.__regOverlayDebug.dump()    // raw array
 *   window.__regOverlayDebug.clear()   // wipe the log
 *
 * Each entry: { t, event, data, url }. 100-entry FIFO cap.
 *
 * Event vocabulary (post auth-form-post-refactor):
 *   overlay:constructed                       — script booted on page
 *   overlay:open / overlay:close              — visibility transitions
 *   overlay:close-with-pending-login          — close() saw stashed creds
 *   overlay:redirect-to-checkout-*            — checkout intent paths
 *   iframe:message / iframe:message-rejected  — postMessage in/out gate
 *   iframe:sso-context-received               — SPA acked SSO_CONTEXT
 *   login:duplicate-suppressed                — mutex blocked re-fire
 *   login:preflight-start                     — fetch POST kicked off
 *   login:preflight-response                  — fetch resolved (any)
 *   login:preflight-retry-scheduled           — bounce-back, retry queued
 *   login:preflight-retry-scheduled-after-throw — network err, retry queued
 *   login:preflight-treated-as-error          — gave up, told iframe error
 *   login:preflight-failed                    — network err, gave up
 *   login:preflight-ok                        — creds validated, stashed
 *   login:customer-data-sent                  — iframe watchdog satisfied
 *   login:closed-before-resolution            — late-resolution branch
 *   login:form-submit                         — top-level form POST fired
 *   login:form-skipped-no-creds               — no stashed creds at submit
 *   login:form-missing / login:form-malformed — DOM integrity failures
 * ═══════════════════════════════════════════════════════════════════ */
window.__DD_REG_OVERLAY_DEBUG_KEY = 'dd_overlay_debug_log';
window.__DD_REG_OVERLAY_BUILD = 'auth-form-post-2026-05-04';

window.__regOverlayDebug = {
  dump: function () {
    try {
      return JSON.parse(localStorage.getItem(window.__DD_REG_OVERLAY_DEBUG_KEY) || '[]');
    } catch (e) { return []; }
  },
  print: function () {
    var log = this.dump();
    try {
      console.table(log.map(function (e) {
        return {
          time: (e.t || '').split('T')[1] ? (e.t || '').split('T')[1].replace('Z', '') : e.t,
          event: e.event,
          data: e.data ? JSON.stringify(e.data).slice(0, 120) : '',
          url: e.url
        };
      }));
    } catch (err) { console.log(log); }
    return log;
  },
  clear: function () {
    try { localStorage.removeItem(window.__DD_REG_OVERLAY_DEBUG_KEY); } catch (e) {}
    return 'cleared';
  },
  build: window.__DD_REG_OVERLAY_BUILD
};

class RegistrationOverlay extends HTMLElement {
  constructor() {
    super();
    this._activeElement = null;
    this._ready = false;
    this._prefetched = null;
    this._currentPath = '/auth';
    this._prefetchScheduled = false;
    this._closeCleanupTimer = null;
    this._onCloseTransitionEnd = null;
    this._timer = null;
    this._isAddingToCart = false;
    /* Credentials stashed by `_handleShopifyLogin` after a successful
       preflight POST to /account/login validates them. Consumed by
       `_submitAutoLoginForm()` (called from `close()` or
       `REDIRECT_TO_CHECKOUT`) to perform a top-level form POST that
       atomically commits the customer session cookie and lands the
       user authenticated.

       Why memory and not sessionStorage: the credentials must NEVER
       persist beyond this overlay session. They live on this instance
       only as long as the user is in the post-success → dismiss
       window. Cleared on consumption, on overlay teardown, and on
       any subsequent `_handleShopifyLogin` call so a stale stash from
       an abandoned attempt can't piggyback on the next login.

       Shape: { email: string, password: string, returnTo: string }
       or null. */
    this._pendingPostLoginCreds = null;

    /* Mutex against the SPA's observed double-fire of USER_LOGIN.
       The iframe app's SignInForm fires LOGIN once on submit and
       useCloseIframe() flushes a pending login again right before
       CLOSE_IFRAME, so a single user action can post USER_LOGIN twice.
       Without this guard the theme runs two preflight POSTs to
       /account/login per user action — wasted Shopify request budget
       and Cloudflare bot-score risk. Set on entry to
       `_handleShopifyLogin`, cleared in `.finally()` of the preflight
       chain. Per implementation-tracing rule, every mutex resets on
       exception. */
    this._loginInFlight = false;
    this._completeRegistrationTracked = false;
    this._paidMetaSignupTracked = false;
    this._debug = false;
    try {
      this._debug =
        (window.location && window.location.search && window.location.search.indexOf('ddregdebug=1') !== -1) ||
        localStorage.getItem('ddRegOverlayDebug') === '1';
    } catch (e) {}

    this.iframe = this.querySelector('#RegOverlayIframe');
    this.skeleton = this.querySelector('#RegOverlaySkeleton');
    this.fallback = this.querySelector('#RegOverlayFallback');
    this.retryBtn = this.querySelector('#RegOverlayRetry');
    this.container = this.querySelector('#RegOverlayDialog');
    this.backdrop = this.querySelector('.reg-overlay__backdrop');

    this.dragHandle = this.querySelector('.reg-overlay__drag-handle');

    this._onMessage = this._onMessage.bind(this);

    /* Attach the postMessage listener IMMEDIATELY at construction.
       The iframe in `snippets/registration-overlay.liquid` has
       `src="/apps/apply/auth"` with `loading="eager"`, so the browser
       starts fetching the SPA at HTML parse time — well before
       `_scheduleAutoPrefetch()` schedules its ~100ms-post-DCL
       `prefetch()` call (which is where the listener was previously
       added for the auto-prefetch path). On a fast network the React
       app inside the iframe can boot and post `IFRAME_READY` in that
       gap, and any save-data / 2g connection skips `_scheduleAutoPrefetch`
       entirely (so the listener wouldn't attach until the user clicks
       a trigger and `open()` runs) — making the lost-message case
       deterministic on those connections.

       Also bootstrap `_prefetched = '/auth'` to match the static
       iframe src. Otherwise `prefetch('/auth')` always falls through
       its `_prefetched === requestedPath` early-return, resets
       `_ready = false`, hides the skeleton on top of the already-
       loading iframe, and waits for an `IFRAME_READY` that may have
       already fired and been observed (or — pre this fix — already
       fired and been lost).

       Browser dedupes identical (handler, type, options) addEventListener
       calls, so the redundant attaches in `prefetch()`, `open()`,
       and `_bindLoginUrlInterceptor()` are no-ops; they remain in
       place because `close()` removes the listener and they re-attach
       it for subsequent open cycles. */
    window.addEventListener('message', this._onMessage);
    this._prefetched = '/auth';

    this._bindEvents();
    this._bindTriggers();
    this._bindLoginUrlInterceptor();
    this._bindDrag();
    this._scheduleAutoPrefetch();

    /* Capture landing-page utm_* before later client navigations strip
       the query string. META_CONTEXT reads this cache. Persist a
       first-touch paid-Meta flag so PaidMetaSignup can fire after
       the query string is gone. CompleteRegistration stays ungated. */
    RegistrationOverlay._readUtmParams();
    RegistrationOverlay._persistPaidMetaFlag();

    RegistrationOverlay._dbg('overlay:constructed', { build: window.__DD_REG_OVERLAY_BUILD });

    /* Pick up post-apply redirects (?applied=1) on initial load. The
       postMessage path (_onMessage → APPLICATION_SUBMITTED) handles the
       in-iframe case; this covers config-driven redirects set inside
       Helium's form rules. Idempotent — calling twice just re-writes the
       same cookie value. */
    RegistrationOverlay._ingestAppliedQueryParam();

    /* `/account/login` and `/account/register` are never valid
       destinations in this theme — both UIs live inside this cross-
       domain iframe. `templates/customers/login.json` →
       dd-login-redirect.liquid and `templates/customers/register.json`
       → dd-register-redirect.liquid server-side redirect those hits to
       `/?auth=login` and `/?auth=register` respectively; this picks up
       the query param and auto-opens the correct pane. */
    this._ingestAuthQueryParam();
  }

  /* ---------------------------------------------------------------------
   * Iframe origin / path strategy
   *
   * The React registration app is now served first-party via Shopify App
   * Proxy at `https://<storefront>/apps/apply/*`, which Shopify forwards
   * to `https://apply.dropdeadextensions.com/*`. Loading the iframe on
   * the storefront origin lets it set/read the Shopify customer session
   * cookie directly — the previous cross-origin setup left the cookie on
   * `apply.dropdeadextensions.com`, so the storefront couldn't see the
   * authenticated state on reload.
   *
   *   ORIGIN          — primary postMessage origin (storefront).
   *                     Used as `targetOrigin` when posting INTO the
   *                     iframe and as the primary accept-list entry
   *                     when receiving messages OUT of the iframe.
   *   LEGACY_ORIGIN   — fallback. Kept on the receive side so any
   *                     preview / legacy theme that still loads the
   *                     iframe directly from apply.dropdeadextensions.com
   *                     keeps working until that path is fully retired.
   *   APP_PROXY_PATH  — relative path prefix (no protocol, no host) so
   *                     the browser resolves iframe URLs against the
   *                     current storefront origin automatically. The
   *                     proxy must be installed on this store; if it's
   *                     missing the iframe load falls back to the
   *                     overlay's "Registration is temporarily
   *                     unavailable" state via the existing timeout.
   * ------------------------------------------------------------------ */
  static get ORIGIN() {
    return window.location.origin;
  }

  static get LEGACY_ORIGIN() {
    return 'https://apply.dropdeadextensions.com';
  }

  static get APP_PROXY_PATH() {
    return '/apps/apply';
  }

  static get TIMEOUT_MS() {
    return 15000;
  }

  /* Build the iframe URL relative to the storefront origin. The browser
     resolves `/apps/apply/auth` against `window.location.origin`, so
     this works on the production domain, on `*.myshopify.com` preview,
     and inside Shopify's theme-editor preview pane without any host
     detection. */
  static _buildSrc(path) {
    return RegistrationOverlay.APP_PROXY_PATH + (path || '/auth');
  }

  /* postMessage targetOrigin for messages INTO the iframe.
     First-party App Proxy (`/apps/apply`) lives on the storefront
     origin. The apply subdomain is only used when the iframe document
     was actually loaded from there (legacy / preview). */
  static _iframeTargetOrigin(iframe) {
    try {
      var src = iframe && iframe.getAttribute('src');
      if (!src) return window.location.origin;
      var origin = new URL(src, window.location.href).origin;
      if (origin === RegistrationOverlay.LEGACY_ORIGIN) {
        return RegistrationOverlay.LEGACY_ORIGIN;
      }
    } catch (e) { /* fall through */ }
    return window.location.origin;
  }

  static _readCookie(name) {
    try {
      var escaped = String(name).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      var match = document.cookie.match(new RegExp('(?:^|; )' + escaped + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : '';
    } catch (e) {
      return '';
    }
  }

  /* First-touch paid-Meta flag for custom event PaidMetaSignup
     on Shopify Sales Channel pixel 1142892540694499.
     CompleteRegistration stays ungated (all signups).
     fbclid is NOT a qualifier (Meta adds it to every click and
     _fbc lasts ~90 days). */
  static get PAID_META_COOKIE() {
    return 'dd_meta_ad';
  }

  static get PAID_META_SESSION_KEY() {
    return 'dd_meta_ad_v1';
  }

  static get PAID_META_TTL_SEC() {
    return 7 * 24 * 60 * 60;
  }

  static _isPaidMetaSource(source) {
    return /^(facebook|fb|instagram|ig|meta)$/.test(String(source || '').toLowerCase());
  }

  static _isPaidMetaMedium(medium) {
    var m = String(medium || '').toLowerCase();
    return m === 'paid' || m === 'cpc' || m === 'ppc'
      || m === 'paidsocial' || m === 'paid_social' || m === 'paid-social';
  }

  static _landingIsPaidMeta() {
    try {
      if (sessionStorage.getItem(RegistrationOverlay.PAID_META_SESSION_KEY) === '1') {
        return true;
      }
    } catch (e) { /* sessionStorage blocked */ }
    try {
      var params = new URLSearchParams(window.location.search);
      if (params.get('dd_meta_ad') === '1') return true;
    } catch (e) { /* URLSearchParams unavailable */ }
    var utm = RegistrationOverlay._readUtmParams();
    return RegistrationOverlay._isPaidMetaSource(utm.utm_source)
      && RegistrationOverlay._isPaidMetaMedium(utm.utm_medium);
  }

  static _hasPaidMetaFlag() {
    if (RegistrationOverlay._readCookie(RegistrationOverlay.PAID_META_COOKIE) === '1') {
      return true;
    }
    return RegistrationOverlay._landingIsPaidMeta();
  }

  static _persistPaidMetaFlag() {
    if (RegistrationOverlay._readCookie(RegistrationOverlay.PAID_META_COOKIE) === '1') {
      return;
    }
    var fromUrl = false;
    try {
      fromUrl = new URLSearchParams(window.location.search).get('dd_meta_ad') === '1';
    } catch (e) { fromUrl = false; }
    if (fromUrl) {
      try { sessionStorage.setItem(RegistrationOverlay.PAID_META_SESSION_KEY, '1'); } catch (e) {}
    }
    if (!RegistrationOverlay._landingIsPaidMeta()) return;
    try { sessionStorage.setItem(RegistrationOverlay.PAID_META_SESSION_KEY, '1'); } catch (e) {}
    try {
      document.cookie = RegistrationOverlay.PAID_META_COOKIE
        + '=1; Max-Age=' + RegistrationOverlay.PAID_META_TTL_SEC
        + '; Path=/; SameSite=Lax';
    } catch (e) { /* cookie blocked */ }
  }

  /* Meta signals for the apply SPA.
     First-party `/apps/apply` can read `_fbp` / `_fbc` itself. We still
     send them on META_CONTEXT as a backup, plus `parent_url` so CAPI
     uses the storefront page rather than `/apps/apply/auth`.
     Query-string forwarding is only for a cross-origin iframe on
     apply.dropdeadextensions.com, which cannot see parent cookies. */
  static _srcIsLegacyOrigin(src) {
    try {
      return new URL(src, window.location.href).origin === RegistrationOverlay.LEGACY_ORIGIN;
    } catch (e) {
      return false;
    }
  }

  static _readUtmParams() {
    var out = {};
    try {
      var raw = sessionStorage.getItem('dd_meta_utm_v1');
      if (raw) out = JSON.parse(raw) || {};
    } catch (e) { out = {}; }
    try {
      var params = new URLSearchParams(window.location.search);
      var changed = false;
      params.forEach(function (val, key) {
        if (key.indexOf('utm_') !== 0 || !val) return;
        out[key] = val;
        changed = true;
      });
      if (changed) {
        sessionStorage.setItem('dd_meta_utm_v1', JSON.stringify(out));
      }
    } catch (e) { /* sessionStorage / URLSearchParams unavailable */ }
    return out;
  }

  static _readMetaContext() {
    var fbp = RegistrationOverlay._readCookie('_fbp');
    var fbc = RegistrationOverlay._readCookie('_fbc');
    var fbclid = '';
    try {
      fbclid = new URLSearchParams(window.location.search).get('fbclid') || '';
    } catch (e) { fbclid = ''; }
    if (!fbc && fbclid) {
      if (!window.__ddMetaFbc) {
        window.__ddMetaFbc = 'fb.1.' + Date.now() + '.' + fbclid;
      }
      fbc = window.__ddMetaFbc;
    }
    var ctx = {
      fbp: fbp,
      fbc: fbc,
      fbclid: fbclid,
      dd_meta_ad: RegistrationOverlay._hasPaidMetaFlag() ? '1' : '',
      parent_url: window.location.href,
      utm_source: '',
      utm_medium: '',
      utm_campaign: '',
      utm_term: '',
      utm_content: '',
      utm_id: ''
    };
    var utm = RegistrationOverlay._readUtmParams();
    Object.keys(utm).forEach(function (key) {
      if (key.indexOf('utm_') === 0 && utm[key]) ctx[key] = utm[key];
    });
    return ctx;
  }

  static _appendQueryParams(src, params) {
    var url;
    try {
      url = new URL(src, window.location.href);
    } catch (e) {
      return src;
    }
    Object.keys(params).forEach(function (key) {
      if (params[key]) url.searchParams.set(key, params[key]);
    });
    if (src.charAt(0) === '/') {
      return url.pathname + url.search + url.hash;
    }
    return url.href;
  }

  /* Single source of truth for "is this postMessage from a place we
     trust?". The storefront origin is the canonical answer post-proxy;
     the legacy apply origin is allowed so a preview theme that still
     points the iframe at apply.dropdeadextensions.com directly keeps
     working through the cutover. */
  static _isAllowedMessageOrigin(origin) {
    return origin === RegistrationOverlay.ORIGIN
      || origin === RegistrationOverlay.LEGACY_ORIGIN;
  }

  /* ---------------------------------------------------------------------
   * SSO context contract with apply.dropdeadextensions.com
   *
   * When a login attempt originates from a third-party surface (Circle
   * / Syndicate community, embedded partner storefronts, etc.) we
   * thread a small context payload through to the iframe so it can
   * render a customised shell — "Log in to continue to The Syndicate"
   * instead of a generic "Welcome back".
   *
   * Context is delivered via TWO redundant channels:
   *
   * 1. URL query params on the iframe src. The apply app can read
   *    these server-side on first byte, so the customised copy shows
   *    without a loading flash. URL is now relative — App Proxy
   *    rewrites `/apps/apply/*` to the apply origin server-side.
   *      /apps/apply/login?sso=syndicate
   *        &return_url=%2Fpages%2Fsyndicate-bridge
   *        &display_name=The+Syndicate
   *
   * 2. A `SSO_CONTEXT` postMessage fired immediately after
   *    IFRAME_READY. Same payload, JSON-serialised. Lets the iframe
   *    acknowledge receipt and lets us push updated context on a
   *    subsequent open() without reloading the iframe.
   *
   *    Expected iframe ack (optional but recommended):
   *      { type: 'SSO_CONTEXT_RECEIVED', data: { source: '...' } }
   *
   * Recognised `source` values (iframe app owns presentation for each):
   *   - 'syndicate' | 'circle'  → Syndicate community SSO
   *   - 'checkout'              → Checkout gating (future)
   *   - 'pdp-review'            → Review-gate login (future)
   *
   * Contract notes:
   *   - Never send secrets through this channel (URL params leak via
   *     browser history / server logs). Only non-sensitive identifiers.
   *   - Always set postMessage targetOrigin to the iframe's actual origin
   *     (`_iframeTargetOrigin`: storefront for `/apps/apply`, otherwise
   *     https://apply.dropdeadextensions.com) so Chrome doesn't leak the
   *     payload to a navigated-away page.
   *   - Iframe MUST treat any unknown source as a neutral login. A new
   *     source id can be added here without shipping the iframe.
   * ------------------------------------------------------------------ */
  static get SSO_SOURCE_KEY() {
    return 'dd_post_login_source';
  }

  /* Map of source identifiers → display names so the theme can pre-fill
     a human-readable label even if the iframe app doesn't recognise the
     key yet. Iframe is free to override with its own mapping. */
  static get SSO_SOURCE_LABELS() {
    return {
      syndicate: 'The Syndicate',
      circle: 'The Syndicate',
      checkout: 'Checkout',
      'pdp-review': 'your review'
    };
  }

  /* Build the `?sso=…&return_url=…&display_name=…` tail appended to
     the iframe src when SSO context is active. Meta click ids stay
     off first-party `/apps/apply` URLs (SPA reads cookies). If the
     iframe src is actually the apply subdomain, append fbp/fbc/fbclid
     /parent_url because that document cannot see storefront cookies. */
  _buildSrcWithContext(path) {
    var src = RegistrationOverlay._buildSrc(path);
    var ctx = RegistrationOverlay._readSsoContext();
    var query = [];
    if (ctx.source) query.push('sso=' + encodeURIComponent(ctx.source));
    if (ctx.returnUrl) query.push('return_url=' + encodeURIComponent(ctx.returnUrl));
    if (ctx.displayName) query.push('display_name=' + encodeURIComponent(ctx.displayName));
    if (query.length) {
      src = src + (src.indexOf('?') === -1 ? '?' : '&') + query.join('&');
    }
    if (RegistrationOverlay._srcIsLegacyOrigin(src)) {
      src = RegistrationOverlay._appendQueryParams(src, RegistrationOverlay._readMetaContext());
    }
    return src;
  }

  /* Read the SSO context the theme has stashed for this login attempt.
     Both keys are cleared by `_consumePostLoginReturn` / its source
     sibling after the login navigation fires, so stale context can't
     leak into a later unrelated login. */
  static _readSsoContext() {
    var out = { source: '', returnUrl: '', displayName: '' };
    try {
      out.returnUrl = sessionStorage.getItem(RegistrationOverlay.POST_LOGIN_RETURN_KEY) || '';
      out.source = sessionStorage.getItem(RegistrationOverlay.SSO_SOURCE_KEY) || '';
    } catch (e) {}
    if (out.source) {
      var labels = RegistrationOverlay.SSO_SOURCE_LABELS;
      if (labels[out.source]) out.displayName = labels[out.source];
    }
    return out;
  }

  static _consumeSsoSource() {
    try {
      var v = sessionStorage.getItem(RegistrationOverlay.SSO_SOURCE_KEY);
      if (v) sessionStorage.removeItem(RegistrationOverlay.SSO_SOURCE_KEY);
      return v || '';
    } catch (e) {
      return '';
    }
  }

  /* ---------------------------------------------------------------------
   * Applied-for-pro-account breadcrumb
   *
   * Drop Dead uses Helium Customer Fields for pro-account applications.
   * Per Helium's docs (help.heliumdev.com/en/articles/6311748), NO Shopify
   * customer record is created during the pending-approval window — the
   * pending record lives only inside the Helium app. That means theme
   * Liquid has zero Shopify-visible signal to distinguish "just applied,
   * awaiting approval" from a cold guest.
   *
   * Shopify Liquid also does NOT expose a `cookies` global for arbitrary
   * first-party cookies (see shopify.dev / Stack Overflow — it's been a
   * hard limitation forever), so we can't do this server-side either. The
   * gate has to live fully in the client.
   *
   * `_markApplied()` writes three coordinated signals when the apply SPA
   * signals success:
   *   1. `localStorage['dd_color_ring_guest_offer_v1']` — PRIMARY signal.
   *      Read by dd-sticky-footer.js → `updateGuestOfferRow()` to decide
   *      whether to unhide the "Discount unlocked" row. Shape matches
   *      that function's existing reader (submittedAt, expiresAt).
   *   2. `dd_applied` first-party cookie — secondary/redundant signal.
   *      Survives localStorage being cleared (e.g. privacy extensions).
   *      Also forward-compat with any future server-side read path
   *      (Oxygen / Hydrogen / edge renderers).
   *   3. `dd:applied` CustomEvent on `document` — same-session / same-tab
   *      update channel so the sticky footer can flip the row immediately
   *      without needing a reload.
   *
   * TTL on both persistent signals matches the color-ring guest discount
   * window (48h default, overridable via `settings.color_ring_guest_discount_hours`)
   * so the banner copy stays truthful. If the applicant hasn't been
   * approved + activated within that window, the PDP falls back to the
   * normal "Apply" CTA — same as every other guest.
   *
   * Two ways _markApplied() gets called:
   *   1. Iframe posts `{ type: 'APPLICATION_SUBMITTED' }` on successful
   *      form submit (preferred path — requires 1 line in the SPA).
   *   2. Apply SPA redirects back to the theme with `?applied=1` on the
   *      URL (Helium's "Redirect customers after form submission" config).
   *      The overlay picks this up on page load.
   * ------------------------------------------------------------------ */

  static get APPLIED_COOKIE_NAME() {
    return 'dd_applied';
  }

  /* Default TTL matches the color-ring guest discount window (48h).
     Exposed as a static so future copy changes only need a single edit. */
  static get APPLIED_COOKIE_MAX_AGE_SECONDS() {
    return 60 * 60 * 48;
  }

  /* Must stay in sync with dd-sticky-footer.js → `getGuestOfferState()`.
     Any schema bump requires rolling the version suffix in BOTH places. */
  static get APPLIED_STORAGE_KEY() {
    return 'dd_color_ring_guest_offer_v1';
  }

  static _setAppliedCookie() {
    try {
      /* `SameSite=Lax` is correct here — the cookie must be read on
         same-site navigation. `Secure` is mandatory on HTTPS-only stores.
         We deliberately don't set `HttpOnly` because this is a
         presentation-layer hint, not an auth token, and we need JS read
         access anyway. */
      var attrs = [
        RegistrationOverlay.APPLIED_COOKIE_NAME + '=1',
        'Path=/',
        'Max-Age=' + RegistrationOverlay.APPLIED_COOKIE_MAX_AGE_SECONDS,
        'SameSite=Lax',
      ];
      if (window.location && window.location.protocol === 'https:') {
        attrs.push('Secure');
      }
      document.cookie = attrs.join('; ');
    } catch (e) {
      /* cookies can be disabled in private/strict modes — we still have
         localStorage as the primary signal, so the row will still light
         up for this session. */
    }
  }

  static _writeAppliedStorage() {
    try {
      var now = Date.now();
      var expiresAt = now + RegistrationOverlay.APPLIED_COOKIE_MAX_AGE_SECONDS * 1000;
      localStorage.setItem(
        RegistrationOverlay.APPLIED_STORAGE_KEY,
        JSON.stringify({ submittedAt: now, expiresAt: expiresAt })
      );
    } catch (e) {
      /* Storage quota / private mode failures — non-fatal. The cookie
         path will still fire, and the user can always just reload after
         approval. */
    }
  }

  static _dispatchAppliedEvent() {
    try {
      document.dispatchEvent(new CustomEvent('dd:applied', { bubbles: true }));
    } catch (e) {
      /* CustomEvent constructor missing on very old browsers — the row
         will still flip on the next page load via localStorage. */
    }
  }

  /* Canonical "the user just finished applying" side effect. Idempotent —
     calling twice within the TTL just refreshes timestamps. Callers don't
     need to reach into the three underlying writers individually. */
  static _markApplied() {
    RegistrationOverlay._writeAppliedStorage();
    RegistrationOverlay._setAppliedCookie();
    RegistrationOverlay._dispatchAppliedEvent();
  }

  /* ---------------------------------------------------------------------
   * Post-registration eligibility cache (founder-call routing)
   *
   * The apply SPA decides whether a brand-new pro registrant qualifies
   * for a 1:1 founder onboarding call (e.g. based on stated monthly
   * order volume, account type, auto-approval). It reports that verdict
   * on the `REGISTRATION_SUCCESS` message, which fires while the user is
   * still looking at the iframe's own success screen — BEFORE the
   * `CLOSE_IFRAME` that actually triggers the theme-side redirect.
   *
   * We stash the verdict in sessionStorage so the later CLOSE_IFRAME
   * handler can branch the redirect target even if that close message
   * doesn't re-send the eligibility payload. sessionStorage (not
   * localStorage) so it dies with the tab — a verdict from one
   * registration must never leak into an unrelated later session.
   *
   * Only `founderCallEligible` drives the redirect; the other fields are
   * cached for the welcome page / analytics to read if needed. The cache
   * is read-and-cleared the moment the redirect target is computed (see
   * the CLOSE_IFRAME registration_complete branch) so a stale verdict
   * can't survive into a subsequent flow in the same tab.
   * ------------------------------------------------------------------ */
  static get POST_REGISTER_KEY() {
    return 'dde_post_register';
  }

  static _cachePostRegisterEligibility(data) {
    try {
      sessionStorage.setItem(
        RegistrationOverlay.POST_REGISTER_KEY,
        JSON.stringify({
          founderCallEligible: !!data.founderCallEligible,
          monthlyOrderVolume: data.monthlyOrderVolume,
          accountType: data.accountType,
          autoApproved: !!data.autoApproved
        })
      );
    } catch (e) {
      /* Private mode / quota — non-fatal. The CLOSE_IFRAME payload's own
         founderCallEligible (when present) still drives routing; only the
         "verdict reported on SUCCESS but omitted on CLOSE" path degrades
         to the non-eligible default, which is the safe direction. */
    }
  }

  static _readPostRegisterEligibility() {
    try {
      var raw = sessionStorage.getItem(RegistrationOverlay.POST_REGISTER_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  }

  static _clearPostRegisterEligibility() {
    try {
      sessionStorage.removeItem(RegistrationOverlay.POST_REGISTER_KEY);
    } catch (e) {}
  }

  /* One-shot check on construction: if the page URL carries `?applied=1`
     (Helium post-submit redirect), mark applied and strip the param so it
     doesn't persist across navigation/share. Must be called from the
     constructor, not `connectedCallback`, so the storage write lands
     before dd-sticky-footer.js reads it on its own connect. */
  static _ingestAppliedQueryParam() {
    try {
      if (!window.location || !window.location.search) return;
      var params = new URLSearchParams(window.location.search);
      if (params.get('applied') !== '1') return;

      RegistrationOverlay._markApplied();

      /* Clean the URL so refreshes/shares don't re-trigger or look ugly
         in the address bar. `replaceState` leaves no history entry. */
      params.delete('applied');
      var qs = params.toString();
      var clean = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
      window.history.replaceState(window.history.state, '', clean);
    } catch (e) {
      /* URL API unavailable in very old browsers — not worth polyfilling
         since the postMessage path will still catch the successful-submit
         case for those few users. */
    }
  }

  prefetch(triggeredBy) {
    if (this.classList.contains('active')) return;
    var path = triggeredBy && triggeredBy.getAttribute
      ? triggeredBy.getAttribute('data-overlay-path')
      : null;
    var requestedPath = path || '/auth';
    /* Use the context-aware src so the prefetch warms the iframe at
       the SAME URL we'll navigate it to on open() — otherwise the
       browser cache miss on open defeats the prefetch entirely. */
    var targetSrc = this._buildSrcWithContext(requestedPath);

    if (this._ready && this._currentPath === requestedPath && this.iframe.getAttribute('src') === targetSrc) {
      return;
    }
    if (this._prefetched === requestedPath && this.iframe.getAttribute('src') === targetSrc) {
      return;
    }

    this._currentPath = requestedPath;
    this._prefetched = requestedPath;
    this._ready = false;
    this.skeleton.hidden = false;
    this.fallback.hidden = true;
    this.iframe.style.opacity = '0';

    if (this.iframe.getAttribute('src') !== targetSrc) {
      this.iframe.src = targetSrc;
    }
    window.addEventListener('message', this._onMessage);
  }

  // Kick off a background prefetch of the most likely path (/login if a
  // login trigger exists, otherwise the first trigger's path) shortly
  // after DOMContentLoaded. This way, by the time a user clicks
  // "Login to shop" the iframe is already loaded and IFRAME_READY has
  // fired, so open() skips straight to the hydrated view instead of
  // showing skeleton → swap.
  //
  // Earlier this gated on `requestIdleCallback` with a 1200ms timeout,
  // which meant the iframe fetch could be delayed by up to ~1.2s after
  // the page was interactive. On a typical homepage scroll, users
  // click "Login to shop" within 5–30s of arrival, but the worst case
  // (immediate click on a returning visitor) caught the iframe still
  // unloaded — defeating the prefetch entirely. We now fire ~100ms
  // after DOMContentLoaded: late enough that the hero LCP / above-
  // the-fold images don't compete for bandwidth, early enough that
  // even an impatient first click finds a warm iframe.
  //
  // The save-data / 2g/3g bailout still applies — we never want to
  // burn a phone-data user's allotment on a resource they didn't
  // request.
  _scheduleAutoPrefetch() {
    var self = this;

    try {
      var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (conn && (conn.saveData || conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g')) {
        self._warn('Auto-prefetch skipped: save-data or slow connection');
        return;
      }
    } catch (e) {}

    var kick = function () {
      if (self._prefetchScheduled) return;
      self._prefetchScheduled = true;
      /* 100ms is enough breathing room for the browser to finish
         parsing critical CSS / firing LCP for the hero, but short
         enough that the iframe fetch is in-flight before the user
         can scroll past the hero and hit a CTA. */
      setTimeout(function () { self._autoPrefetch(); }, 100);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', kick, { once: true });
    } else {
      kick();
    }
  }

  _autoPrefetch() {
    if (this.classList.contains('active')) return;

    var triggers = document.querySelectorAll('[data-registration-overlay-open]');
    if (!triggers.length) return;

    var chosen = null;
    for (var i = 0; i < triggers.length; i++) {
      var path = triggers[i].getAttribute('data-overlay-path');
      if (path === '/login') { chosen = triggers[i]; break; }
      if (!chosen) chosen = triggers[i];
    }

    var chosenPath = chosen.getAttribute('data-overlay-path') || '/auth';
    if (
      this._ready &&
      this._currentPath === chosenPath &&
      this.iframe.getAttribute('src') === this._buildSrcWithContext(chosenPath)
    ) {
      return;
    }

    this._warn('Auto-prefetch starting', { path: chosenPath });
    this.prefetch(chosen);
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (this._onCloseTransitionEnd) {
      this.removeEventListener('transitionend', this._onCloseTransitionEnd);
      this._onCloseTransitionEnd = null;
    }
    clearTimeout(this._closeCleanupTimer);
    this._closeCleanupTimer = null;
    if (triggeredBy) this._activeElement = triggeredBy;
    window.addEventListener('message', this._onMessage);

    this._closeOtherDrawers();

    var path = triggeredBy && triggeredBy.getAttribute
      ? triggeredBy.getAttribute('data-overlay-path')
      : null;
    var requestedPath = path || '/auth';
    /* Context-aware src: the iframe gets ?sso=…&return_url=…&display_name=…
       appended whenever a Circle/Syndicate (or future SSO partner)
       stash is present. Identical URL to what prefetch used, so the
       warm-iframe fast path below still hits. */
    var targetSrc = this._buildSrcWithContext(requestedPath);
    var isWarmForRequestedPath =
      this._ready &&
      this._currentPath === requestedPath &&
      this.iframe.getAttribute('src') === targetSrc;

    if (isWarmForRequestedPath) {
      this.skeleton.hidden = true;
      this.fallback.hidden = true;
      this.iframe.style.opacity = '1';
    } else if (this._currentPath !== requestedPath || this.iframe.getAttribute('src') !== targetSrc) {
      this._resetState();
      this._currentPath = requestedPath;
      this._prefetched = requestedPath;
      this.iframe.src = targetSrc;
    } else {
      this.skeleton.hidden = false;
      this.fallback.hidden = true;
      this.iframe.style.opacity = '0';
    }

    clearTimeout(this._timer);
    this._timer = null;
    if (!isWarmForRequestedPath) {
      this._timer = setTimeout(() => {
        if (!this._ready) {
          this.skeleton.hidden = true;
          this.fallback.hidden = false;
          this._warn('Overlay timeout: iframe did not become ready within TIMEOUT_MS');
        }
      }, RegistrationOverlay.TIMEOUT_MS);
    }

    requestAnimationFrame(() => {
      this.classList.add('active');
      document.body.classList.add('reg-overlay-open');
    });

    this.addEventListener('transitionend', () => {
      if (this.container) this.container.focus({ preventScroll: true });
      if (typeof trapFocus === 'function') trapFocus(this.container, this.container);
    }, { once: true });
  }

  close() {
    if (!this.classList.contains('active')) return;

    var stackFrames = '';
    try {
      stackFrames = (new Error().stack || '').split('\n').slice(1, 6).join(' | ');
    } catch (e) {}
    RegistrationOverlay._dbg('overlay:close', { stack: stackFrames });

    /* Capture pending-login state BEFORE running the rest of close().
       If a successful preflight stashed credentials during this overlay
       session, we need to submit the hidden auto-login form so the
       browser performs a top-level POST that atomically commits the
       Shopify customer session cookie. Without this, the iframe shows
       its success screen but the user lands on a guest-rendered page
       on dismiss — the cookie-vs-replication race that motivated this
       whole flow.

       We DON'T null _pendingPostLoginCreds here — _submitAutoLoginForm
       consumes it. If we cleared first and submit ran second, we'd
       lose the creds. */
    var hasPendingLogin = !!this._pendingPostLoginCreds;
    if (hasPendingLogin) {
      RegistrationOverlay._dbg('overlay:close-with-pending-login', null);
    }

    this.classList.remove('active');
    document.body.classList.remove('reg-overlay-open');

    if (typeof removeTrapFocus === 'function') removeTrapFocus(this._activeElement);

    window.removeEventListener('message', this._onMessage);
    clearTimeout(this._timer);
    this._timer = null;

    var self = this;
    function finalizeCloseCleanup() {
      if (self.classList.contains('active')) return;
      if (self._onCloseTransitionEnd) {
        self.removeEventListener('transitionend', self._onCloseTransitionEnd);
        self._onCloseTransitionEnd = null;
      }
      clearTimeout(self._closeCleanupTimer);
      self._closeCleanupTimer = null;
      self.iframe.removeAttribute('src');
      self._resetState();
      self._prefetched = null;
      self._currentPath = '/auth';
    }
    this._onCloseTransitionEnd = function (evt) {
      if (evt && evt.target !== self) return;
      finalizeCloseCleanup();
    };
    this.addEventListener('transitionend', this._onCloseTransitionEnd, { once: true });
    clearTimeout(this._closeCleanupTimer);
    this._closeCleanupTimer = setTimeout(finalizeCloseCleanup, 450);

    this._activeElement = null;

    /* Submit the hidden auto-login form as a top-level navigation if
       a successful preflight stashed credentials. Defer one frame so
       the close animation has a chance to start; the form-POST
       navigation will tear down the document mid-animation, which is
       fine — no animation is more important than the user landing
       authenticated.

       If no pending login (modal opened but never submitted, dismissal
       before any USER_LOGIN message, etc.), close cleanly without any
       navigation — the user stays on the current URL as a guest.

       TRIAL OFFER RELOAD: if the SPA collected marketing consent during
       this overlay session (signalled by the dd-trial-unlocked class
       that dd-trial-unlock.js markUnlocked() puts on <html>), every
       surface on the page is stale — they still render the locked tease.
       Reloading lets Liquid re-derive the unlocked state from the
       metafield and repaint all surfaces with the real code. This
       mirrors the reload that the trial-unlock modal's own close()
       already does. Skipped when hasPendingLogin because the form POST
       is itself a full navigation that reloads the page. */
    if (hasPendingLogin) {
      var self = this;
      requestAnimationFrame(function () {
        self._submitAutoLoginForm();
      });
    } else if (document.documentElement.classList.contains('dd-trial-unlocked')) {
      try {
        window.location.reload();
      } catch (e) {
        /* noop */
      }
    }
  }

  _resetState() {
    this._ready = false;
    clearTimeout(this._timer);
    this._timer = null;
    this.skeleton.hidden = false;
    this.fallback.hidden = true;
    this.iframe.style.opacity = '0';
  }

  _warn(message, details) {
    if (!this._debug) return;
    if (details) {
      console.warn('[RegistrationOverlay] ' + message, details);
    } else {
      console.warn('[RegistrationOverlay] ' + message);
    }
  }

  /* Persistent event log that survives page reloads. Silent by default.
     Enable with `?ddregdebug=1` on the page URL or
     `localStorage.ddRegOverlayDebug='1'`. When disabled, this is a
     no-op so production overlays don't write a localStorage entry per
     event nor spam the console. `__regOverlayDebug.dump/print/clear`
     remain callable in either state — they just operate on whatever
     was previously captured (empty array if nothing). */
  static _dbg(event, data) {
    var enabled = false;
    try {
      enabled =
        (window.location && window.location.search && window.location.search.indexOf('ddregdebug=1') !== -1) ||
        localStorage.getItem('ddRegOverlayDebug') === '1';
    } catch (e) {}
    if (!enabled) return;

    try {
      var key = window.__DD_REG_OVERLAY_DEBUG_KEY;
      var raw = localStorage.getItem(key);
      var log = raw ? JSON.parse(raw) : [];
      log.push({
        t: new Date().toISOString(),
        event: event,
        data: data == null ? null : data,
        url: window.location.pathname + window.location.search
      });
      if (log.length > 100) log.shift();
      localStorage.setItem(key, JSON.stringify(log));
    } catch (e) {}
  }

  _markReady(reason) {
    /* Idempotency guard. The SPA has been observed firing IFRAME_READY
       repeatedly (~every 250ms throughout the iframe lifetime — likely
       a polling loop tied to its own readiness watchdog), and without
       this guard each duplicate would re-run all visibility writes
       AND re-post SSO_CONTEXT to the iframe. The visibility writes
       are harmless but every redundant SSO_CONTEXT roundtrip wastes
       the postMessage budget and floods the trace log. The path-
       change escape hatch below covers the legitimate re-arm case
       where a soft NAVIGATE moved _currentPath out from under us
       and the iframe genuinely re-booted at the new path. */
    if (this._ready && this._prefetched === this._currentPath) {
      return;
    }

    this._ready = true;
    this._prefetched = this._currentPath || '/auth';
    clearTimeout(this._timer);
    this._timer = null;
    this.skeleton.hidden = true;
    this.fallback.hidden = true;
    this.iframe.style.opacity = '1';
    this._warn('Overlay marked ready via ' + reason);

    /* Push SSO context once the iframe is alive. Redundant with the
       URL-param channel (covers server-side first paint) but lets the
       iframe re-render branded copy if it was booted generically and
       the context stash was populated after navigation (e.g. a
       "Continue to Syndicate" CTA that opens the already-warm iframe). */
    this._pushSsoContext();
    this._pushMetaContext();
  }

  /* Serialise the current SSO stash and post it to the iframe. The
     iframe is expected to handle at minimum `source`, `returnUrl`,
     and `displayName`, and may use `_raw` keys for forward-compat
     experimentation without a theme deploy. */
  _pushSsoContext() {
    var ctx = RegistrationOverlay._readSsoContext();
    if (!ctx.source && !ctx.returnUrl) return;
    this._sendToIframe('SSO_CONTEXT', {
      source: ctx.source,
      returnUrl: ctx.returnUrl,
      displayName: ctx.displayName
    });
  }

  _pushMetaContext() {
    /* parent_url is the load-bearing first-party field: the iframe
       document URL is /apps/apply/*, not the PDP/home they applied from.
       Cookie fields are backup; same-origin SPA should prefer document.cookie. */
    this._sendToIframe('META_CONTEXT', RegistrationOverlay._readMetaContext());
  }

  _onMessage(e) {
    if (!RegistrationOverlay._isAllowedMessageOrigin(e.origin)) {
      RegistrationOverlay._dbg('iframe:message-rejected', { reason: 'origin', got: e.origin });
      this._warn('Ignored message: unexpected origin', {
        got: e.origin,
        expected: RegistrationOverlay.ORIGIN,
        legacy: RegistrationOverlay.LEGACY_ORIGIN
      });
      return;
    }
    if (this.iframe && e.source !== this.iframe.contentWindow) {
      RegistrationOverlay._dbg('iframe:message-rejected', { reason: 'source' });
      this._warn('Ignored message: unexpected message source');
      return;
    }
    if (!e.data || !e.data.type) {
      RegistrationOverlay._dbg('iframe:message-rejected', { reason: 'no-type' });
      this._warn('Ignored message: missing message type');
      return;
    }

    RegistrationOverlay._dbg('iframe:message', {
      type: e.data.type,
      hasPayload: !!e.data.data,
      payloadKeys: e.data.data ? Object.keys(e.data.data) : []
    });

    switch (e.data.type) {
      case 'IFRAME_READY':
        this._markReady('IFRAME_READY');
        break;

      case 'CLOSE_IFRAME': {
        /* Post-registration handoff. When the apply SPA emits
             { type: 'CLOSE_IFRAME', data: { reason: 'registration_complete' } }
           after a successful registration, we set the
           `dde_just_registered` sessionStorage flag (read by
           sections/dd-call-booker.liquid for the GA4 analytics
           push) and — if the customizer kill switch is on —
           redirect by ELIGIBILITY: a founder-call-eligible
           registrant lands on the founder-call page, everyone
           else on the welcome page.

           Eligibility is decided by the apply SPA and reported on
           the REGISTRATION_SUCCESS message (cached to sessionStorage
           via `_cachePostRegisterEligibility`). This handler prefers
           a `founderCallEligible` flag on THIS close payload if the
           SPA re-sends it, else falls back to the cached verdict,
           else treats the user as non-eligible. The cache is
           read-and-cleared the moment the target URL is computed.

           Gating rules:
             • Kill switch OFF (default) → behave exactly like
               before (`this.close()`), no redirect — and drop any
               cached eligibility verdict. Lets the funnel be turned
               on in customizer once both pages are finalized
               without a deploy.
             • Kill switch ON + theme-side preflight creds present
               → stash the eligibility-resolved `returnTo` on the
               existing `_pendingPostLoginCreds` so the existing
               auto-login form POST in `close()`/`_submitAutoLoginForm`
               establishes the session AND lands on the right page in
               one navigation (no two-redirect race). This
               INTENTIONALLY overrides any prior `dd_post_login_return`
               stash — registration completion is a strictly more
               recent signal than wherever the user came from before
               opening the overlay.
             • Kill switch ON + no preflight creds → iframe SPA
               established the session server-side before posting
               registration_complete. Dismiss the overlay, then
               navigate at the top level to the eligibility-resolved
               target.

           Analytics flag is always set (regardless of kill
           switch). That lets us observe registration completions
           even while the funnel is dark, and avoids the edge case
           where the kill switch flips between the registration POST
           and the next page load. */
        var ddCloseData = (e.data && e.data.data) || {};
        if (ddCloseData.reason === 'registration_complete') {
          try { sessionStorage.setItem('dde_just_registered', '1'); } catch (_) {}

          var ddRedirectCfg = (typeof window !== 'undefined' && window.DD_POST_REG_REDIRECT) || {};
          if (!ddRedirectCfg.enabled) {
            RegistrationOverlay._dbg('overlay:registration-complete-noredirect', {
              reason: 'kill-switch-off'
            });
            /* Kill switch off → no redirect funnel at all. Drop any cached
               eligibility verdict so it can't leak into a later session. */
            RegistrationOverlay._clearPostRegisterEligibility();
            this.close();
            break;
          }

          /* Founder-call routing (QUALIFIED-ONLY). A qualifying registrant
             is redirected to the founder-call page; everyone else is NOT
             redirected at all (see the eligibility gate below).
             Eligibility precedence: the verdict on THIS
             CLOSE_IFRAME payload wins if present (most recent, authoritative),
             otherwise fall back to the value the SPA cached on the earlier
             REGISTRATION_SUCCESS message. Missing on both → treated as
             non-eligible (the safe default — never auto-book a founder call
             for someone the SPA didn't qualify). */
          var ddCachedElig = RegistrationOverlay._readPostRegisterEligibility();
          var ddFounderEligible = (ddCloseData.founderCallEligible != null)
            ? !!ddCloseData.founderCallEligible
            : !!ddCachedElig.founderCallEligible;

          /* Read-and-clear: the verdict has now been consumed, so the
             cache must not survive into a later session. */
          RegistrationOverlay._clearPostRegisterEligibility();

          /* QUALIFIED-ONLY funnel: only a founder-call-eligible registrant
             is redirected. Everyone else falls through to the default
             overlay close and stays where they registered — identical to
             kill-switch-off behavior. */
          if (!ddFounderEligible) {
            RegistrationOverlay._dbg('overlay:registration-complete-noredirect', {
              reason: 'not-founder-eligible'
            });
            this.close();
            break;
          }

          var ddTargetUrl = (typeof ddRedirectCfg.founderCallUrl === 'string' && ddRedirectCfg.founderCallUrl)
            ? ddRedirectCfg.founderCallUrl
            : '/pages/welcome';

          if (this._pendingPostLoginCreds && this._pendingPostLoginCreds.email) {
            this._pendingPostLoginCreds.returnTo = ddTargetUrl;
            RegistrationOverlay._dbg('overlay:registration-complete-form-redirect', {
              targetUrl: ddTargetUrl,
              founderEligible: ddFounderEligible
            });
            this.close();
            break;
          }

          RegistrationOverlay._dbg('overlay:registration-complete-direct-redirect', {
            targetUrl: ddTargetUrl,
            founderEligible: ddFounderEligible
          });
          this.close();
          try {
            window.location.assign(ddTargetUrl);
          } catch (_) {
            window.location.href = ddTargetUrl;
          }
          break;
        }
        this.close();
        break;
      }

      case 'REGISTRATION_SUCCESS':
        /* Optional intermediate signal the apply SPA may post
           between submission success and overlay dismissal (the
           SPA may show its own "welcome" screen for a moment
           before emitting CLOSE_IFRAME). Set the analytics flag
           and cache the founder-call eligibility verdict — never
           navigate here, because the user may still be interacting
           with the iframe. Final navigation (and the eligibility
           branch) happens on the subsequent CLOSE_IFRAME with
           reason=registration_complete.

           Caching the verdict here means the redirect still routes
           correctly even if the SPA omits the eligibility fields on
           its CLOSE_IFRAME payload (it reported them on success, and
           CLOSE just signals "user dismissed").

           Kill switch does NOT gate either write (same rationale as
           in CLOSE_IFRAME above — observe completions even while the
           redirect funnel is dark, and avoid an edge case where the
           switch flips between this message and the close). */
        try { sessionStorage.setItem('dde_just_registered', '1'); } catch (_) {}
        RegistrationOverlay._cachePostRegisterEligibility(e.data.data || {});
        break;

      case 'USER_LOGIN':
        this._handleShopifyLogin(e.data.data || {});
        break;

      case 'USER_FORGOT_PASSWORD':
        this._handleShopifyRecover(e.data.data || {});
        break;

      case 'ADD_TO_CART':
        this._handleIframeAddToCart(e.data.data || {});
        break;

      case 'ADD_TO_CART_AND_CHECKOUT':
        this._handleIframeAddToCartAndCheckout(e.data.data || {});
        break;

      case 'REDIRECT_TO_CHECKOUT': {
        var code = e.data && e.data.data ? e.data.data.discountCode : null;
        var checkoutUrl = code
          ? '/checkout?discount=' + encodeURIComponent(code)
          : '/checkout';

        /* If a successful preflight just stashed credentials (user
           logged in inside the overlay then immediately clicked
           "checkout"), route through _submitAutoLoginForm so the
           top-level form POST establishes the session AND lands on
           checkout in one navigation. Without this, a bare
           location.href to /checkout would race the not-yet-committed
           session and Shopify would bounce the user to /account/login
           — same class of bug as the dismiss-then-reload race we
           fixed for normal close.

           If no pending login (e.g. already-authenticated customer
           initiating checkout, or guest checkout from an apply flow),
           preserve the original direct navigation. */
        if (this._pendingPostLoginCreds) {
          this._pendingPostLoginCreds.returnTo = checkoutUrl;
          RegistrationOverlay._dbg('overlay:redirect-to-checkout-with-pending-login', {
            checkoutUrl: checkoutUrl
          });
          this._submitAutoLoginForm();
        } else {
          RegistrationOverlay._dbg('overlay:redirect-to-checkout-direct', {
            checkoutUrl: checkoutUrl
          });
          window.location.href = checkoutUrl;
        }
        break;
      }

      /* Contract the apply SPA emits:
       *   window.parent.postMessage({
       *     type: 'APPLICATION_SUBMITTED',
       *     metaEventId: '<uuid>'
       *   }, targetOrigin);
       * Origin + source checks at the top of _onMessage already protect
       * against spoofing. metaEventId is required for the browser Pixel
       * CompleteRegistration event so Meta can dedupe it against CAPI.
       *
       * IMPORTANT: this handler MUST NOT close the overlay. The iframe
       * owns its own post-submit UX — success screen, offer, error
       * messaging — and any of those screens may need to stay visible
       * until the user dismisses them. The theme's job here is purely
       * to set the breadcrumb cookie so the storefront recognises the
       * "applied" state on subsequent page loads (PDPs, navigation,
       * etc.) even if the user closes the tab before dismissing the
       * success screen.
       *
       * The iframe controls dismissal by emitting `CLOSE_IFRAME` when
       * the user clicks its post-submit dismiss button. The user can
       * also dismiss via backdrop click or Escape key (see _bindEvents).
       *
       * Intentionally idempotent — cookies API ignores duplicate writes
       * of the same value, so repeated messages (e.g. retry flows) won't
       * extend the TTL unintentionally. If we later want a sliding TTL,
       * this is the single place to change.
       */
      case 'APPLICATION_SUBMITTED': {
        RegistrationOverlay._markApplied();
        this._trackCompleteRegistration(e.data);
        this._trackPaidMetaSignup(e.data);
        break;
      }

      /* Contract the apply SPA emits when it finishes collecting (or
       * re-checking) marketing consent:
       *   window.parent.postMessage({
       *     type: 'SUBSCRIPTION_STATUS',
       *     data: { discountUnlocked: true, discountCode: '<reward code>' }
       *   }, 'https://<store-domain>');
       *
       * SAME-SESSION OPTIMISTIC BOOST ONLY. This is not an unlock
       * authority and must never be treated as one:
       *
       *   - The durable gate is the customer metafield
       *     custom.salon_trial_opted_in, written server-side by the
       *     unlock-trial-offer App Proxy function after Shopify itself
       *     confirms the consent state. Liquid re-derives lock state from
       *     that metafield on every page load, so anything set here is
       *     overwritten by the truth on the next navigation.
       *   - Nothing is persisted. A class on <html> dies with the tab.
       *     Persisting it would let a stale localStorage entry keep a
       *     tease hidden long after the metafield said otherwise, and the
       *     failure mode would be an invisible offer with no code.
       *   - No code is revealed from this payload. The teases carry no
       *     code node to fill (INVARIANT 1 in
       *     snippets/dd-trial-offer-eligibility.liquid), so a "reveal"
       *     here would have to synthesise markup that the server has
       *     already decided this customer may not see.
       *
       * What it buys: the registration flow's own success screen and the
       * theme agree within the same session, so a customer who consents
       * inside the SPA does not see a locked tease behind the overlay
       * while their metafield write settles.
       */
      case 'SUBSCRIPTION_STATUS': {
        var subData = e.data.data || {};
        if (subData.discountUnlocked === true) {
          try {
            if (window.DDTrialUnlock && window.DDTrialUnlock.markUnlocked) {
              window.DDTrialUnlock.markUnlocked();
            } else {
              document.documentElement.classList.add('dd-trial-unlocked');
            }
          } catch (_e) { /* cosmetic only — never break the overlay */ }
        }
        RegistrationOverlay._dbg('iframe:subscription-status', {
          discountUnlocked: subData.discountUnlocked === true
        });
        break;
      }

      case 'SSO_CONTEXT_RECEIVED': {
        /* Iframe ack documented at the SSO context contract block above
           (see SSO_SOURCE_KEY / _readSsoContext comments). Confirms the
           SPA received the SSO_CONTEXT push (URL params + postMessage).
           The theme has nothing to do here other than record receipt
           for diagnostics — the SSO stash is consumed on login success,
           not on context ack, so a missing/late ack is non-fatal.
           Defensive read so a future iframe build that ships a
           different payload shape can never throw inside _onMessage. */
        var ackSource = (e.data && e.data.data && typeof e.data.data.source === 'string')
          ? e.data.data.source
          : null;
        RegistrationOverlay._dbg('iframe:sso-context-received', { source: ackSource });
        break;
      }
    }
  }

  _sendToIframe(type, data) {
    if (this.iframe && this.iframe.contentWindow) {
      this.iframe.contentWindow.postMessage(
        { type: type, data: data },
        RegistrationOverlay._iframeTargetOrigin(this.iframe)
      );
    }
  }

  /* Browser Pixel CompleteRegistration on Shopify Sales Channel
     dataset 1142892540694499. Fires for every successful apply.
     Shares metaEventId with the SPA's CAPI event so Meta dedupes
     the pair. No-op when fbq is absent or we already fired for
     this overlay session. */
  _trackCompleteRegistration(msg) {
    if (this._completeRegistrationTracked) return;
    if (typeof window.fbq !== 'function') return;
    var eventId = msg && (msg.metaEventId || (msg.data && msg.data.metaEventId));
    if (!eventId) return;
    try {
      window.fbq(
        'track',
        'CompleteRegistration',
        { content_name: 'Pro account application' },
        { eventID: eventId }
      );
      this._completeRegistrationTracked = true;
      RegistrationOverlay._dbg('pixel:complete-registration', {
        hasEventId: true
      });
    } catch (err) {
      try { console.warn('[RegistrationOverlay] CompleteRegistration pixel failed', err); } catch (e) {}
    }
  }

  /* Custom event for Ads Manager reporting. Only when this browser
     had a paid-Meta landing (dd_meta_ad=1 or facebook+paid UTMs)
     within the 7-day first-touch window.

     Shopify Sales Channel lives in a web-pixel sandbox, so parent
     window.fbq often never reaches dataset 1142892540694499.
     Publish `dd:paid_meta_signup` for the Customer events pixel
     (same pattern as NCPurchase). Keep window.fbq as a backup.
     Custom conversion event name: PaidMetaSignup (exact match). */
  _trackPaidMetaSignup(msg) {
    if (this._paidMetaSignupTracked) return;
    RegistrationOverlay._persistPaidMetaFlag();
    if (!RegistrationOverlay._hasPaidMetaFlag()) {
      RegistrationOverlay._dbg('pixel:paid-meta-signup-skipped', {
        reason: 'not-paid-meta'
      });
      return;
    }
    var eventId = msg && (msg.metaEventId || (msg.data && msg.data.metaEventId));
    if (!eventId) return;
    var pixelEventId = eventId + '-paidmeta';
    var sent = false;
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish('dd:paid_meta_signup', {
          eventID: pixelEventId,
          content_name: 'Pro account application'
        });
        sent = true;
      }
    } catch (err) {
      try { console.warn('[RegistrationOverlay] PaidMetaSignup analytics.publish failed', err); } catch (e) {}
    }
    try {
      if (typeof window.fbq === 'function') {
        window.fbq(
          'trackCustom',
          'PaidMetaSignup',
          { content_name: 'Pro account application' },
          { eventID: pixelEventId }
        );
        sent = true;
      }
    } catch (err) {
      try { console.warn('[RegistrationOverlay] PaidMetaSignup pixel failed', err); } catch (e) {}
    }
    if (!sent) return;
    this._paidMetaSignupTracked = true;
    RegistrationOverlay._dbg('pixel:paid-meta-signup', {
      hasEventId: true
    });
  }

  /* USER_LOGIN handler. Two-phase auth:

     1. PREFLIGHT: fetch POST /account/login (with retry-on-bounce-back
        to absorb the eventual-consistency window between Shopify's
        customer-login tier and auth-validation tier). Used purely to
        validate credentials so wrong-password errors render inline in
        the iframe instead of forcing a top-level navigation to
        Shopify's bare login page.

     2. FORM POST (deferred): on preflight success, stash credentials
        on the instance and let the iframe show its "Welcome" screen.
        When the user dismisses (CLOSE_IFRAME / backdrop / Esc / drag),
        `close()` calls `_submitAutoLoginForm()` which submits the
        hidden #RegOverlayAutoLoginForm as a top-level navigation.
        The browser-driven POST lets Shopify commit the customer
        session cookie atomically with the redirect chain it owns,
        eliminating the cookie-vs-session-replication race that
        plagued the old `fetch + reload()` pattern.

     The preflight session cookie left over from phase 1 may or may
     not be honored by the next request — we don't rely on it. The
     form POST creates a fresh session record server-side and the
     browser commits its cookies as part of accepting the navigation
     response, which is the path Shopify's /account/login was designed
     for. */
  _handleShopifyLogin(data) {
    if (!data.email || !data.password) {
      RegistrationOverlay._dbg('login:rejected', {
        reason: 'missing-creds',
        hasEmail: !!data.email,
        hasPassword: !!data.password
      });
      return;
    }

    /* Mutex against the SPA's observed double-fire of USER_LOGIN.
       Two windows to defend:

       1. Concurrent in-flight: the SPA emits two USER_LOGIN messages
          within milliseconds (race between initial submit and
          useCloseIframe()'s pending-login flush). Caught by
          `_loginInFlight`.

       2. Post-success replay: useCloseIframe() flushes the pending
          login right before CLOSE_IFRAME, which lands ~120ms AFTER
          the first preflight resolved (`_loginInFlight` already
          reset). Caught by `_pendingPostLoginCreds` — if creds are
          already stashed from a successful preflight, a duplicate
          USER_LOGIN with the same creds is by definition redundant.

       Either condition fires this guard, costing zero extra
       /account/login requests per user action regardless of which
       SPA path triggered the duplicate. */
    if (this._loginInFlight || this._pendingPostLoginCreds) {
      RegistrationOverlay._dbg('login:duplicate-suppressed', {
        email: data.email,
        reason: this._loginInFlight ? 'in-flight' : 'creds-stashed'
      });
      return;
    }
    this._loginInFlight = true;

    var self = this;
    RegistrationOverlay._dbg('login:preflight-start', { email: data.email });
    this._sendToIframe('LOGIN_STATUS', { status: 'submitting' });

    self._loginPreflightAttempt(data, 0);
  }

  /* Preflight POST to /account/login. One-shot retry-with-backoff for
     the just-activated/just-registered eventual-consistency window
     (~500-2000ms between create-customer and customer-login tier
     replication). The retry is silent from the iframe's POV — we
     don't change LOGIN_STATUS between attempts so the user sees a
     continuous spinner. After 2 attempts we treat further failures
     as genuinely-wrong-password.

     On success, this stashes credentials on the instance and tells
     the iframe to render its success screen. The actual session-
     establishing form POST is deferred until overlay dismissal — see
     `_submitAutoLoginForm()` and the `close()` / REDIRECT_TO_CHECKOUT
     callers. */
  _loginPreflightAttempt(data, attempt) {
    var self = this;
    var MAX_ATTEMPTS = 2;
    var RETRY_DELAY_MS = 1200;

    var body = new URLSearchParams();
    body.append('form_type', 'customer_login');
    body.append('utf8', '\u2713');
    body.append('customer[email]', data.email);
    body.append('customer[password]', data.password);

    fetch('/account/login', {
      method: 'POST',
      body: body,
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      /* `redirect: 'follow'` is load-bearing for the preflight's
         purpose (validate creds before showing success in iframe).
         Shopify 302s out of /account/login on every outcome — to
         /account on success, to /account/login itself with the form
         re-rendered + error flash on failure. Following the chain
         lets us read `res.url` and detect the bounce-back signature.
         Without follow, we'd false-positive on wrong passwords. */
      credentials: 'include',
      redirect: 'follow'
    }).then(function (res) {
      var finalPath = '';
      try {
        finalPath = new URL(res.url, window.location.origin).pathname;
      } catch (e) {}

      RegistrationOverlay._dbg('login:preflight-response', {
        type: res.type,
        status: res.status,
        ok: res.ok,
        redirected: res.redirected,
        url: res.url,
        finalPath: finalPath
      });

      var endedAtLoginPage =
        finalPath === '/account/login' || finalPath === '/account/login/';

      if (!res.ok || endedAtLoginPage) {
        if (attempt < MAX_ATTEMPTS - 1) {
          RegistrationOverlay._dbg('login:preflight-retry-scheduled', {
            attempt: attempt,
            nextAttempt: attempt + 1,
            delayMs: RETRY_DELAY_MS,
            finalPath: finalPath
          });
          setTimeout(function () {
            self._loginPreflightAttempt(data, attempt + 1);
          }, RETRY_DELAY_MS);
          return;
        }

        RegistrationOverlay._dbg('login:preflight-treated-as-error', {
          ok: res.ok,
          endedAtLoginPage: endedAtLoginPage,
          finalPath: finalPath,
          attempts: attempt + 1
        });
        self._sendToIframe('LOGIN_STATUS', {
          status: 'error',
          message: 'Invalid email or password.'
        });
        self._loginInFlight = false;
        return;
      }

      /* Preflight passed — credentials are valid. Stash them so the
         deferred form POST can use them on dismiss. Capture the SSO
         returnTo NOW (consume from sessionStorage before another login
         attempt could inherit stale context) and route it to the
         right hidden field at form-submit time based on URL shape.
         _consumeSsoSource clears the SSO source key for the same
         reason. */
      var returnTo = RegistrationOverlay._consumePostLoginReturn();
      RegistrationOverlay._consumeSsoSource();

      /* No explicit SSO / deep-link target was stashed — default to the
         URL the user was viewing when they triggered the overlay so
         they land back on the same product, blog post, search results
         page, etc. after auth. Without this, _submitAutoLoginForm's
         hardcoded `'/'` fallback (kept as a last resort) sends every
         non-SSO login to the homepage, which is what users observe as
         the "I clicked login on a PDP and got bounced home" bug.

         window.location is still the originating page at this point
         (the form POST hasn't fired yet — we're inside the iframe's
         postMessage callback) and modal focus-trap prevents the user
         from navigating between open() and preflight-success, so this
         is a stable read.

         Routed through `_sanitizePostLoginReturn` so the same open-
         redirect protections apply: same-origin paths only, plus the
         explicit Syndicate / apply allowlist used elsewhere. If
         sanitisation rejects the URL for any reason, we fall through
         to the empty string and `_submitAutoLoginForm` keeps its
         emergency `'/'` default. */
      if (!returnTo) {
        try {
          var current =
            window.location.pathname +
            window.location.search +
            window.location.hash;
          var safeCurrent = RegistrationOverlay._sanitizePostLoginReturn(current);
          if (safeCurrent) returnTo = safeCurrent;
        } catch (e) {}
      }

      self._pendingPostLoginCreds = {
        email: data.email,
        password: data.password,
        returnTo: returnTo || ''
      };
      RegistrationOverlay._dbg('login:preflight-ok', {
        returnTo: returnTo || null
      });

      /* Iframe contract: LOGIN_STATUS:success first, then CUSTOMER_DATA
         within 3 seconds. SignInForm.tsx → loginUpdate has a 3s
         watchdog that surfaces "Login failed. Please contact support"
         if CUSTOMER_DATA never arrives. We satisfy it minimally —
         isLoggedIn:true triggers iframe navigation to /already-logged-
         in which unmounts SignInForm and clears the timer. */
      self._sendToIframe('LOGIN_STATUS', {
        status: 'success',
        returnTo: returnTo || null
      });
      self._sendToIframe('CUSTOMER_DATA', {
        isLoggedIn: true,
        email: data.email || null,
        firstName: null,
        accessToken: null,
        expiresAt: null
      });
      RegistrationOverlay._dbg('login:customer-data-sent', {
        email: data.email || null
      });

      self._loginInFlight = false;

      /* Late-resolution race: if the user dismissed the overlay BEFORE
         this preflight resolved (e.g. login took longer than the
         iframe's 3s watchdog and the user clicked the close-X on the
         "couldn't auto-sign-in" screen), close() already ran and
         doesn't know about the credentials we just stashed. Mirror
         close()'s deferred-submit logic here so both orderings
         (close-then-resolve vs. resolve-then-close) end with the user
         authenticated. */
      if (!self.classList.contains('active')) {
        RegistrationOverlay._dbg('login:closed-before-resolution', {
          hasCreds: !!self._pendingPostLoginCreds
        });
        requestAnimationFrame(function () {
          self._submitAutoLoginForm();
        });
      }
    }).catch(function (err) {
      if (attempt < MAX_ATTEMPTS - 1) {
        RegistrationOverlay._dbg('login:preflight-retry-scheduled-after-throw', {
          attempt: attempt,
          error: String(err && err.message || err)
        });
        setTimeout(function () {
          self._loginPreflightAttempt(data, attempt + 1);
        }, RETRY_DELAY_MS);
        return;
      }
      RegistrationOverlay._dbg('login:preflight-failed', {
        error: String(err && err.message || err),
        attempts: attempt + 1
      });
      self._sendToIframe('LOGIN_STATUS', {
        status: 'error',
        message: 'Something went wrong. Please try again.'
      });
      self._loginInFlight = false;
    });
  }

  /* Submit the hidden auto-login form as a top-level navigation. This
     is what actually establishes the customer session — preflight only
     validates credentials, this is the request whose `Set-Cookie` the
     browser commits as part of accepting the response. Single source
     of truth for the deferred submit; called from close() (when
     `_pendingPostLoginCreds` exists at dismissal time) and from the
     late-resolution branch of `_loginPreflightAttempt` (when
     dismissal beat the preflight resolve).

     `return_to` is Shopify's canonical post-login redirect field for
     relative paths. `checkout_url` is the legacy field for absolute
     URLs (SSO bridges to syndicate.dropdeadextensions.com etc.).
     Routing returnTo to the right field based on URL shape avoids
     Shopify silently ignoring an absolute URL passed to return_to. */
  _submitAutoLoginForm() {
    var creds = this._pendingPostLoginCreds;
    if (!creds) {
      RegistrationOverlay._dbg('login:form-skipped-no-creds', null);
      return;
    }
    this._pendingPostLoginCreds = null;

    var form = document.getElementById('RegOverlayAutoLoginForm');
    if (!form) {
      RegistrationOverlay._dbg('login:form-missing', null);
      return;
    }

    var emailInput = form.querySelector('input[name="customer[email]"]');
    var passwordInput = form.querySelector('input[name="customer[password]"]');
    var returnToInput = form.querySelector('input[name="return_to"]');
    var checkoutUrlInput = form.querySelector('input[name="checkout_url"]');

    if (!emailInput || !passwordInput || !returnToInput || !checkoutUrlInput) {
      RegistrationOverlay._dbg('login:form-malformed', {
        hasEmail: !!emailInput,
        hasPassword: !!passwordInput,
        hasReturnTo: !!returnToInput,
        hasCheckoutUrl: !!checkoutUrlInput
      });
      return;
    }

    emailInput.value = creds.email;
    passwordInput.value = creds.password;

    var rt = creds.returnTo || '/';
    if (/^https?:\/\//i.test(rt)) {
      checkoutUrlInput.value = rt;
      returnToInput.value = '/';
    } else {
      returnToInput.value = rt;
      checkoutUrlInput.value = '';
    }

    RegistrationOverlay._dbg('login:form-submit', { returnTo: rt });
    form.submit();
  }

  _handleShopifyRecover(data) {
    if (!data.email) return;
    var self = this;

    this._sendToIframe('FORGOT_PASSWORD_STATUS', { status: 'submitting' });

    var body = new URLSearchParams();
    body.append('form_type', 'recover_customer_password');
    body.append('utf8', '\u2713');
    body.append('email', data.email);

    fetch('/account/recover', {
      method: 'POST',
      body: body,
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      redirect: 'manual'
    }).then(function () {
      self._sendToIframe('FORGOT_PASSWORD_STATUS', { status: 'success' });
    }).catch(function () {
      self._sendToIframe('FORGOT_PASSWORD_STATUS', {
        status: 'error',
        message: 'Something went wrong. Please try again.'
      });
    });
  }

  _handleIframeAddToCart(data) {
    this._performIframeAddToCart(data, { sendStatus: true, emitCartEvents: true });
  }

  _handleIframeAddToCartAndCheckout(data) {
    var self = this;
    this._performIframeAddToCart(data, { sendStatus: true, emitCartEvents: true }).then(function (result) {
      if (!result || !result.ok) return;
      window.location.href = self._buildCheckoutUrl(data && data.discountCode);
    });
  }

  _buildCheckoutUrl(discountCode) {
    return discountCode
      ? '/checkout?discount=' + encodeURIComponent(discountCode)
      : '/checkout';
  }

  _performIframeAddToCart(data, options) {
    var sendStatus = !!(options && options.sendStatus);
    var emitCartEvents = options && options.emitCartEvents !== false;

    var variantId = parseInt(data && data.id, 10);
    var quantity = parseInt(data && data.quantity, 10);
    var requestId =
      data && typeof data.requestId === 'string' && data.requestId.trim()
        ? data.requestId.trim()
        : 'req_' + Date.now() + '_' + Math.floor(Math.random() * 1000000);

    if (!variantId || variantId <= 0) {
      if (sendStatus) {
        this._sendToIframe('ADD_TO_CART_STATUS', {
          status: 'error',
          message: 'Missing variant id.',
          requestId: requestId
        });
      }
      return Promise.resolve({ ok: false, requestId: requestId, message: 'Missing variant id.' });
    }

    if (!quantity || quantity <= 0) {
      quantity = 1;
    }

    if (this._isAddingToCart) {
      if (sendStatus) {
        this._sendToIframe('ADD_TO_CART_STATUS', {
          status: 'error',
          message: 'Cart action already in progress.',
          requestId: requestId
        });
      }
      return Promise.resolve({ ok: false, requestId: requestId, message: 'Cart action already in progress.' });
    }

    var self = this;
    this._isAddingToCart = true;
    if (sendStatus) {
      this._sendToIframe('ADD_TO_CART_STATUS', { status: 'submitting', requestId: requestId });
    }

    var payload = {
      items: [{ id: variantId, quantity: quantity }]
    };

    // Bundle cart drawer/icon sections with add.js to avoid an extra request.
    if (document.querySelector('cart-drawer')) {
      payload.sections = 'cart-drawer,cart-icon-bubble';
      payload.sections_url = window.location.pathname;
    }

    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (res) {
        return res.json().then(function (json) {
          return { ok: res.ok, json: json };
        });
      })
      .then(function (result) {
        if (!result.ok) {
          var message = (result.json && (result.json.description || result.json.message)) || 'Unable to add item.';
          if (sendStatus) {
            self._sendToIframe('ADD_TO_CART_STATUS', {
              status: 'error',
              message: message,
              requestId: requestId
            });
          }
          return { ok: false, requestId: requestId, message: message };
        }

        if (emitCartEvents) {
          var cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
            try {
              cartDrawer.renderContents(result.json);
            } catch (err) {
              self._warn('cart-drawer renderContents failed after ADD_TO_CART', err);
            }
          }

          var _cartPayload = { source: 'registration-overlay', cartData: result.json, cart: result.json };
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, _cartPayload);
          }
          try { document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: _cartPayload })); } catch (_e) {}
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }

        if (sendStatus) {
          self._sendToIframe('ADD_TO_CART_STATUS', {
            status: 'success',
            requestId: requestId,
            id: variantId,
            quantity: quantity
          });
        }
        return { ok: true, requestId: requestId, id: variantId, quantity: quantity, cart: result.json };
      })
      .catch(function () {
        var message = 'Something went wrong. Please try again.';
        if (sendStatus) {
          self._sendToIframe('ADD_TO_CART_STATUS', {
            status: 'error',
            message: message,
            requestId: requestId
          });
        }
        return { ok: false, requestId: requestId, message: message };
      })
      .finally(function () {
        self._isAddingToCart = false;
      });
  }

  _bindEvents() {
    this.addEventListener('click', (e) => {
      if (!this.container || !this.container.contains(e.target)) {
        this.close();
      }
    });

    this.addEventListener('keyup', (e) => {
      if (e.key === 'Escape') this.close();
    });

    if (this.iframe) {
      this.iframe.addEventListener('load', () => {
        /* The iframe `load` event fires for BOTH legitimate boots and
           cross-origin error pages (Shopify App Proxy 404s, app
           backend down, CSP violations that paint about:blank). The
           theme cannot read cross-origin iframe contents to tell the
           two apart, so visibility is owned exclusively by the
           IFRAME_READY postMessage path:
             - On success the SPA posts IFRAME_READY → `_markReady()`
               flips `_ready = true`, hides the skeleton, sets iframe
               opacity to 1, and clears the timeout.
             - On failure the `open()`-installed TIMEOUT_MS timer
               trips → falls back to the in-theme error pane
               (#RegOverlayFallback) instead of leaving the user
               staring at a blank/error iframe.
           This handler used to set `skeleton.hidden = true` and
           `iframe.style.opacity = '1'` unconditionally, which exposed
           error pages for up to TIMEOUT_MS (15s) before the fallback
           tripped — bad UX. It is intentionally a visibility no-op
           now and only carries a debug breadcrumb for the trace log. */
        this._warn('Iframe load event; ready=' + this._ready + ' currentPath=' + this._currentPath);
      });

      this.iframe.addEventListener('error', () => {
        this._warn('Iframe load error event fired');
        clearTimeout(this._timer);
        this.skeleton.hidden = true;
        this.fallback.hidden = false;
      });
    }

    if (this.retryBtn) {
      this.retryBtn.addEventListener('click', () => {
        this._resetState();
        window.addEventListener('message', this._onMessage);
        this.iframe.src = this._buildSrcWithContext(this._currentPath);
        this._timer = setTimeout(() => {
          if (!this._ready) {
            this.skeleton.hidden = true;
            this.fallback.hidden = false;
            this._warn('Retry timeout: iframe did not become ready within TIMEOUT_MS');
          }
        }, RegistrationOverlay.TIMEOUT_MS);
      });
    }
  }

  _bindTriggers() {
    document.querySelectorAll('[data-registration-overlay-open]').forEach((link) => {
      link.setAttribute('role', 'button');
      link.setAttribute('aria-haspopup', 'dialog');
    });

    this._onTriggerClick = (e) => {
      var link = e.target && e.target.closest
        ? e.target.closest('[data-registration-overlay-open]')
        : null;
      if (!link) return;
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      this.open(link);
    };
    document.addEventListener('click', this._onTriggerClick, true);

    this._onTriggerHover = (e) => {
      var link = e.target && e.target.closest
        ? e.target.closest('[data-registration-overlay-open]')
        : null;
      if (!link) return;
      this.prefetch(link);
    };
    document.addEventListener('mouseenter', this._onTriggerHover, true);
    document.addEventListener('touchstart', this._onTriggerHover, { capture: true, passive: true });
  }

  /* ---------------------------------------------------------------------
   * /account/login + /account/register interceptor
   *
   * Both auth UIs in this theme live inside the cross-domain iframe
   * (apply.dropdeadextensions.com/{login,register}) — the native
   * Shopify `/account/login` and `/account/register` pages are NEVER
   * shown to a real user. Intentional triggers already use
   * `[data-registration-overlay-open]`, but old links, manually
   * authored links, third-party snippets, and liquid output from
   * `routes.account_login_url` / `routes.account_register_url` can
   * still emit raw `<a href="/account/login">` or
   * `<a href="/account/register">` into the DOM.
   *
   * This delegate catches those and converts the click into a modal
   * open. Direct navigation (typed URL, external links, bookmarks) is
   * handled server-side by:
   *   - templates/customers/login.json    → sections/dd-login-redirect.liquid
   *     redirects to `/?auth=login`
   *   - templates/customers/register.json → sections/dd-register-redirect.liquid
   *     redirects to `/?auth=register`
   * `_ingestAuthQueryParam` above picks up either query param and
   * auto-opens the matching pane.
   *
   * We explicitly DO NOT swallow:
   *   • Modifier-click / middle-click (lets power users open in new tab)
   *   • Links already wired to the overlay (avoids double-handling)
   *   • target="_blank" links (respect the author's intent)
   *   • Logged-in customers — they don't need either modal, and Shopify
   *     redirects them away from both /account/login and /account/register
   *     natively.
   * ------------------------------------------------------------------ */
  _bindLoginUrlInterceptor() {
    /* Theme Liquid sets `data-glass-customer` on <html> when a customer
       is logged in. Skip wiring entirely so logged-in users can still
       reach /account (our interceptor would otherwise hijack that nav).
       Shopify natively redirects logged-in customers away from
       /account/{login,register} anyway, so the converted modal-open
       would be a useless detour. */
    if (document.documentElement.getAttribute('data-glass-customer') === 'true') return;

    this._onLoginUrlClick = (e) => {
      var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
      if (!a) return;
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (a.hasAttribute('data-registration-overlay-open')) return;
      if (a.getAttribute('target') === '_blank') return;

      /* Map the native Shopify auth URL to the iframe app's pane.
         The apply SPA at apply.dropdeadextensions.com today exposes
         `/login` and the unified `/auth` pane; there is no standalone
         `/register` route — pointing at it renders an in-iframe 404.
         Routing `/account/register` clicks to `/auth` matches the
         working Apply CTAs (dd-login-alert on the PDP, sticky footer,
         syndicate bar, guest teaser) which all rely on the `/auth`
         default for the registration flow. */
      var matchedAuthLink = false;
      var overlayPath = null;
      if (RegistrationOverlay._isLocalPath(a, '/account/login')) {
        matchedAuthLink = true;
        overlayPath = '/login';
      } else if (RegistrationOverlay._isLocalPath(a, '/account/register')) {
        matchedAuthLink = true;
        /* Leave overlayPath unset so the open() flow falls through
           to the JS default (`/auth`). Setting `data-overlay-path`
           to `/register` here would 404 the iframe. */
      }
      if (!matchedAuthLink) return;

      /* Mark this link so hover-prefetch and the overlay open() find
         the right iframe path. Not persisted — just annotated for this
         single call. Skip the attribute entirely for the register
         path so the default kicks in. */
      if (overlayPath) a.setAttribute('data-overlay-path', overlayPath);
      e.preventDefault();
      this.open(a);
    };
    document.addEventListener('click', this._onLoginUrlClick, true);
  }

  /* Resolve an anchor's href against the current origin and match the
     pathname exactly. Avoids false-positives on e.g.
     `https://other.example.com/account/login` or query-stringed siblings. */
  static _isLocalPath(anchor, path) {
    try {
      var url = new URL(anchor.href, window.location.origin);
      if (url.origin !== window.location.origin) return false;
      return url.pathname === path;
    } catch (e) {
      return false;
    }
  }

  /* sessionStorage key used to carry a post-login redirect target
     across the iframe login round-trip. Written by
     `_ingestAuthQueryParam` and `_capturePostLoginReturn` (the latter
     lets any caller stash a target just before opening the modal);
     read by `_handleShopifyLogin` after the Shopify cookie is set,
     right before we navigate out of the current page.

     Lives in sessionStorage (not localStorage) so it clears when the
     tab closes — avoids stale SSO bounces on a fresh visit. */
  static get POST_LOGIN_RETURN_KEY() {
    return 'dd_post_login_return';
  }

  /* One-time reset/activate tokens are staged here by dd-reset-redirect
     and dd-activate-redirect so they never appear in the homepage query
     string (Referer leaks, analytics, server logs). Consumed and cleared
     by `_ingestAuthQueryParam` on the next paint. */
  static get AUTH_RESET_URL_KEY() {
    return 'dd_auth_reset_url';
  }

  static get AUTH_ACTIVATION_URL_KEY() {
    return 'dd_auth_activation_url';
  }

  static get AUTH_EMAIL_HINT_KEY() {
    return 'dd_auth_email_hint';
  }

  static _consumeAuthEmailHintFromStorage() {
    try {
      var hint = sessionStorage.getItem(RegistrationOverlay.AUTH_EMAIL_HINT_KEY) || '';
      sessionStorage.removeItem(RegistrationOverlay.AUTH_EMAIL_HINT_KEY);
      if (
        hint
        && hint.length <= 254
        && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(hint)
      ) {
        return hint;
      }
    } catch (e) {}
    return '';
  }

  /* Only allow return targets that are:
       1. Same-origin relative paths starting with a single `/`
          (rejects protocol-relative `//evil.com/` which would resolve
          to a different origin), OR
       2. The known Syndicate community origin so Circle SSO can
          complete its round-trip.
     Anything else is an open-redirect risk and gets dropped. */
  static _sanitizePostLoginReturn(raw) {
    if (!raw || typeof raw !== 'string') return '';
    var trimmed = raw.trim();
    if (!trimmed) return '';

    if (trimmed.charAt(0) === '/' && trimmed.charAt(1) !== '/') {
      return trimmed;
    }

    try {
      var url = new URL(trimmed, window.location.origin);
      if (url.origin === window.location.origin) return url.pathname + url.search + url.hash;
      if (url.origin === 'https://syndicate.dropdeadextensions.com') return url.href;
      if (url.origin === 'https://apply.dropdeadextensions.com') return url.href;
    } catch (e) {}
    return '';
  }

  /* After `dd-login-redirect` / `dd-register-redirect` /
     `dd-reset-redirect` / `dd-activate-redirect` bounce the user to
     a tagged URL on `/?…`, open the correct overlay pane automatically
     and strip the query params from the address bar so refreshes don't
     re-trigger the modal. Any `return_url` is moved to sessionStorage
     so it survives the round-trip (page reload clears the URL, but
     the storage entry lives until the auth handler consumes it).

     Routing convention — keyed on whichever signal is unambiguous:

       reset_url=<full Shopify reset URL>  (legacy query deep-link)
         → open /reset-password?reset_url=<…> in the iframe.

       auth=reset  (preferred)
         → read reset URL from sessionStorage (`dd_auth_reset_url`),
            then open /reset-password?reset_url=<…>.

       activation_url=<full Shopify activation URL>  (legacy)
         → open /activate-account?activation_url=<…> in the iframe.

       auth=activate  (preferred)
         → read activation URL from sessionStorage (`dd_auth_activation_url`),
            then open /activate-account?activation_url=<…>.

       auth=login    → open /login   (no flow-specific param to use)
       auth=register → open /register

     `reset_url` / `activation_url` legacy query params are still
     accepted for backward compatibility. New redirects use sessionStorage
     plus `auth=reset` / `auth=activate` so tokens never hit the query
     string. `auth=login` / `auth=register` keep the explicit tag because
     those flows have no flow-specific param to key off.

     We validate the URL BEFORE forwarding to the iframe — must be
     same-origin and match the canonical reset/activate path shape.
     Anything that fails validation is dropped silently; the redirect
     section's `?fallback=1` same-origin form is the safety net. */
  _ingestAuthQueryParam() {
    try {
      var params = new URLSearchParams(window.location.search);
      var resetUrlRaw = params.get('reset_url');
      var activationUrlRaw = params.get('activation_url');
      var authMode = params.get('auth');

      /* Preferred path: redirect sections stash the token in
         sessionStorage and bounce to `/?auth=reset` or
         `/?auth=activate` with a clean query string. */
      if (authMode === 'reset') {
        try {
          resetUrlRaw = sessionStorage.getItem(RegistrationOverlay.AUTH_RESET_URL_KEY) || resetUrlRaw;
          sessionStorage.removeItem(RegistrationOverlay.AUTH_RESET_URL_KEY);
        } catch (e) {}
      } else if (authMode === 'activate') {
        try {
          activationUrlRaw = sessionStorage.getItem(RegistrationOverlay.AUTH_ACTIVATION_URL_KEY) || activationUrlRaw;
          sessionStorage.removeItem(RegistrationOverlay.AUTH_ACTIVATION_URL_KEY);
        } catch (e) {}
      }

      /* Optional email hint forwarded by dd-reset-redirect.liquid /
         dd-activate-redirect.liquid when the Shopify notification
         template attached `?email={{ customer.email | url_encode }}`
         to the reset / activate URL. Extracted here, validated against
         a basic email shape, then forwarded into the iframe path so
         the SPA can use it as a fallback when Shopify's
         customerResetByUrl returns customer.email = null (a known
         Storefront API quirk for legacy accounts). Invalid / missing
         is fine — the SPA has secondary fallbacks (sessionStorage
         hint, Admin API lookup in the edge function). */
      var emailHintRaw = params.get('email_hint') || '';
      var emailHint = '';
      if (
        emailHintRaw
        && emailHintRaw.length <= 254
        && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailHintRaw)
      ) {
        emailHint = emailHintRaw;
      }
      if (!emailHint && (authMode === 'reset' || authMode === 'activate')) {
        emailHint = RegistrationOverlay._consumeAuthEmailHintFromStorage();
      }

      var overlayPath;

      if (resetUrlRaw) {
        var validatedReset = RegistrationOverlay._validateAccountActionUrl(
          resetUrlRaw,
          '/account/reset/'
        );
        if (!validatedReset) {
          /* Tampered/malformed — strip and bail. The redirect section
             will have routed here from a clean URL, so this only fires
             for hand-edited URLs. */
          params.delete('reset_url');
          params.delete('email_hint');
          this._cleanQueryStringAndStop(params);
          return;
        }
        overlayPath = '/reset-password?reset_url=' + encodeURIComponent(validatedReset);
        if (emailHint) {
          overlayPath += '&email=' + encodeURIComponent(emailHint);
        }
      } else if (activationUrlRaw) {
        var validatedActivation = RegistrationOverlay._validateAccountActionUrl(
          activationUrlRaw,
          '/account/activate/'
        );
        if (!validatedActivation) {
          params.delete('activation_url');
          params.delete('email_hint');
          this._cleanQueryStringAndStop(params);
          return;
        }
        overlayPath = '/activate-account?activation_url=' + encodeURIComponent(validatedActivation);
        if (emailHint) {
          overlayPath += '&email=' + encodeURIComponent(emailHint);
        }
      } else if (authMode === 'login' || authMode === 'register') {
        /* The apply SPA at apply.dropdeadextensions.com exposes
           `/login` and the unified `/auth` pane only — there is no
           standalone `/register` route, pointing at it renders an
           in-iframe 404. The same file's `_bindLoginUrlInterceptor`
           handles `/account/register` clicks identically (leaves
           overlayPath unset so `/auth` is used as the default), with
           the same comment. `dd-register-redirect.liquid` deep-links
           land here as `?auth=register` and must route to `/auth`
           too — the previous `'/register'` mapping broke every
           register deep-link with a silent in-iframe 404. */
        overlayPath = authMode === 'register' ? '/auth' : '/login';
      } else {
        return;
      }

      var rawReturn = params.get('return_url') || params.get('checkout_url') || '';
      var safeReturn = RegistrationOverlay._sanitizePostLoginReturn(rawReturn);
      if (safeReturn) {
        try {
          sessionStorage.setItem(RegistrationOverlay.POST_LOGIN_RETURN_KEY, safeReturn);
        } catch (e) {}
      } else {
        /* Critical reset: this auth deep-link did not provide a
           fresh return target, so any value left over in sessionStorage
           is from an EARLIER unrelated deep-link the user abandoned
           (e.g. clicked "Apply" on /products/A → didn't sign in →
           navigated to /products/B → clicked Apply again).
           `_consumePostLoginReturn()` is read-and-clear, so without
           an explicit reset here the stale value survives all the
           way through the next successful login and the auto-login
           form POST sends the user to the prior URL instead of
           staying on the current page (or whatever the new deep-link
           intended). Same single-tab session window applies to the
           SSO source key, which `_readSsoContext` pairs with the
           return URL when posting SSO_CONTEXT to the iframe. */
        try {
          sessionStorage.removeItem(RegistrationOverlay.POST_LOGIN_RETURN_KEY);
        } catch (e) {}
      }

      /* Also stash the `from=` source identifier so the iframe can be
         booted with branded copy ("Log in to continue to The Syndicate").
         Sanitised to a whitelist-friendly slug — no arbitrary strings
         end up in URL params or postMessage payloads. */
      var rawFrom = (params.get('from') || '').toLowerCase();

      /* Inverse inference for the Circle / Syndicate SSO chain. When a
         user visits syndicate.dropdeadextensions.com while logged out
         of Shopify, Circle redirects them through Shopify's IDP broker
         (`/apps/idp/moas/broker/login/shopify/idp/proxy`) which in turn
         bounces to `/account/login?return_url=/apps/idp/...`. The IDP
         broker chain ONLY forwards `return_url` — it never carries
         `from=syndicate` — so without this inference the iframe boots
         the generic /login pane instead of the branded "Log in to
         continue to The Syndicate" shell. We detect the broker path
         pattern and synthesize `from=syndicate` so the iframe URL
         picks up `?sso=syndicate&display_name=The+Syndicate` via
         _buildSrcWithContext. The label resolves through
         SSO_SOURCE_LABELS where both 'syndicate' and 'circle' map to
         "The Syndicate". Pattern is anchored to `/apps/idp/` to stay
         specific — other `/apps/...` paths (App Proxy entrypoints for
         Helium, Klaviyo, etc.) must NOT trigger this. */
      if (!rawFrom && safeReturn && /^\/apps\/idp\//.test(safeReturn)) {
        rawFrom = 'syndicate';
      }

      var safeFrom = /^[a-z0-9-]{1,32}$/.test(rawFrom) ? rawFrom : '';
      if (safeFrom) {
        try {
          sessionStorage.setItem(RegistrationOverlay.SSO_SOURCE_KEY, safeFrom);
        } catch (e) {}
      } else {
        /* Same Critical reset as above: this deep-link doesn't carry
           a `from=` source, so any stale slug from a prior abandoned
           SSO flow must not leak into this login's iframe shell copy
           or `_readSsoContext`'s `?sso=` query param on the iframe src. */
        try {
          sessionStorage.removeItem(RegistrationOverlay.SSO_SOURCE_KEY);
        } catch (e) {}
      }

      params.delete('auth');
      params.delete('return_url');
      params.delete('checkout_url');
      params.delete('from');
      params.delete('reset_url');
      params.delete('activation_url');
      params.delete('email_hint');
      var cleanQuery = params.toString();
      var cleanUrl = window.location.pathname
        + (cleanQuery ? '?' + cleanQuery : '')
        + window.location.hash;
      window.history.replaceState(window.history.state, '', cleanUrl);

      /* Open on the next frame so any other overlay wiring (focus
         trap, drawer close, etc.) has time to initialise. Use a
         synthesized trigger so open() knows which pane to load. */
      var self = this;
      var opener = document.createElement('a');
      opener.setAttribute('data-overlay-path', overlayPath);
      requestAnimationFrame(function () { self.open(opener); });
    } catch (e) {
      /* Defensive — URL parsing failures are harmless here. */
    }
  }

  /* Helper used by `_ingestAuthQueryParam` to clean the address bar
     after a bail-out so the malformed param can't re-trigger on
     refresh. Kept tiny — same shape as the success-path cleanup but
     without the modal-open side effect. */
  _cleanQueryStringAndStop(params) {
    try {
      var q = params.toString();
      window.history.replaceState(
        window.history.state,
        '',
        window.location.pathname + (q ? '?' + q : '') + window.location.hash
      );
    } catch (e) {}
  }

  /* Validate a Shopify account-action URL (reset / activate) before
     forwarding it into the iframe. Requirements:
       - parses as a URL (absolute, with scheme + host)
       - host is recognised — accepts EITHER the live storefront origin
         (custom domain like dropdeadextensions.com) OR the shop's
         myshopify permanent domain (drop-dead-2428.myshopify.com).
         Shopify's `customer.reset_password_url` Liquid object is NOT
         guaranteed to use the primary storefront domain — for many
         shops (especially with multi-domain configs, or for legacy
         customer accounts) it returns the myshopify URL even when
         the storefront's primary domain is set to the custom domain.
         The Shopify notification template can't influence this
         either: the URL is generated by Shopify's email pipeline
         from internal config we don't control. So the validator
         here MUST tolerate both forms — same origin OR shop's
         permanent domain — or the silent-drop branch fires for
         every customer with a myshopify-flavoured reset URL and
         the iframe never opens.
       - pathname matches the expected `pathPrefix` followed by exactly
         two URL-safe id/token segments
     Returns the canonical URL string on success, or '' on failure.
     Always reconstructs the URL using `window.location.origin` so
     downstream consumers (the iframe SPA's `customerResetByUrl`
     mutation) see a URL rooted at the live storefront — mirrors
     what `sections/dd-reset-redirect.liquid` does for the path-based
     entry point so both flows produce identical inputs to the SPA.
     Does not throw. */
  static _validateAccountActionUrl(rawUrl, pathPrefix) {
    if (!rawUrl || typeof rawUrl !== 'string') return '';
    var trimmed = rawUrl.trim();
    if (!trimmed) return '';
    var url;
    try {
      url = new URL(trimmed);
    } catch (e) {
      return '';
    }
    /* Allowed origins: the live storefront origin and the shop's
       myshopify permanent domain. Liquid injects the permanent
       domain into `window.__ddShopPermanentDomain` from
       `snippets/registration-overlay.liquid` (server-side, exact
       Shopify-resolved value — no hardcoding). If that global isn't
       set (race against script execution, or a future refactor that
       loses the injection), we still accept the live origin so the
       common case keeps working. */
    var allowedOrigins = [window.location.origin];
    var permanentDomain = (typeof window.__ddShopPermanentDomain === 'string' && window.__ddShopPermanentDomain)
      ? String(window.__ddShopPermanentDomain).replace(/\/$/, '')
      : '';
    if (permanentDomain) {
      var permanentOrigin = 'https://' + permanentDomain;
      if (allowedOrigins.indexOf(permanentOrigin) === -1) {
        allowedOrigins.push(permanentOrigin);
      }
    }
    if (allowedOrigins.indexOf(url.origin) === -1) return '';
    /* Path shape: /account/{reset|activate}/{id}/{token} with optional
       trailing slash. id/token whitelisted to URL-safe base64-ish
       characters so a tampered URL with shell characters or path
       traversal can't slip through. */
    var pathRe = new RegExp(
      '^' + pathPrefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        + '[A-Za-z0-9_-]{1,256}/[A-Za-z0-9_-]{1,256}/?$'
    );
    if (!pathRe.test(url.pathname)) return '';
    /* Reconstruct using `window.location.origin` so the iframe always
       receives a URL on the live storefront, regardless of whether
       Shopify emitted the URL on the custom domain or the myshopify
       permanent domain. Mirrors `sections/dd-reset-redirect.liquid`'s
       reconstruction pattern. */
    return window.location.origin + url.pathname;
  }

  /* Exposed hook for other code paths (e.g. a "Continue to Syndicate"
     button in the review gate flow) to stash a post-login destination
     just before opening the overlay. Runs through the same sanitizer
     so callers can't smuggle arbitrary URLs.

     Optional `source` pairs with `SSO_SOURCE_LABELS` to brand the
     iframe — pass one of 'syndicate' | 'circle' | 'checkout' |
     'pdp-review' (or any future slug the iframe app understands).
     Invalid slugs are silently dropped. */
  static capturePostLoginReturn(target, source) {
    var safe = RegistrationOverlay._sanitizePostLoginReturn(target);
    if (!safe) return false;
    try {
      sessionStorage.setItem(RegistrationOverlay.POST_LOGIN_RETURN_KEY, safe);
      if (source && /^[a-z0-9-]{1,32}$/.test(source)) {
        sessionStorage.setItem(RegistrationOverlay.SSO_SOURCE_KEY, source);
      }
      return true;
    } catch (e) {
      return false;
    }
  }

  /* Read-and-clear. Called by `_handleShopifyLogin` once Shopify has
     set the customer session cookie — we navigate there instead of
     doing a plain `location.reload()`, which would otherwise drop the
     originating SSO context on the floor. */
  static _consumePostLoginReturn() {
    try {
      var val = sessionStorage.getItem(RegistrationOverlay.POST_LOGIN_RETURN_KEY);
      if (!val) return '';
      sessionStorage.removeItem(RegistrationOverlay.POST_LOGIN_RETURN_KEY);
      return RegistrationOverlay._sanitizePostLoginReturn(val);
    } catch (e) {
      return '';
    }
  }

  _bindDrag() {
    if (!this.dragHandle || !('ontouchstart' in window)) return;

    var self = this;
    var startY = 0;
    var currentY = 0;
    var dragging = false;
    var DISMISS_THRESHOLD = 120;

    this.dragHandle.addEventListener('touchstart', function (e) {
      if (!self.classList.contains('active')) return;
      var t = e.touches[0];
      startY = t.clientY;
      currentY = 0;
      dragging = true;
      self.classList.add('dragging');
    }, { passive: true });

    this.dragHandle.addEventListener('touchmove', function (e) {
      if (!dragging) return;
      var t = e.touches[0];
      var dy = t.clientY - startY;
      currentY = Math.max(0, dy);
      self.container.style.transform = 'translateY(' + currentY + 'px)';

      var progress = Math.min(currentY / 300, 1);
      self.backdrop.style.opacity = String(1 - progress * 0.6);
    }, { passive: true });

    this.dragHandle.addEventListener('touchend', function () {
      if (!dragging) return;
      dragging = false;
      self.classList.remove('dragging');
      self.container.style.transform = '';
      self.backdrop.style.opacity = '';

      if (currentY > DISMISS_THRESHOLD) {
        self.close();
      }
    }, { passive: true });

    this.dragHandle.addEventListener('touchcancel', function () {
      if (!dragging) return;
      dragging = false;
      self.classList.remove('dragging');
      self.container.style.transform = '';
      self.backdrop.style.opacity = '';
    }, { passive: true });
  }

  _closeOtherDrawers() {
    var triggerInMenuDrawer = this._activeElement && this._activeElement.closest && this._activeElement.closest('header-drawer');

    var loginDrawer = document.querySelector('login-drawer');
    if (loginDrawer && loginDrawer.classList.contains('active')) {
      loginDrawer.close();
    }
    var cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.classList.contains('active')) {
      cartDrawer.close();
    }

    // Keep the mobile menu open if the trigger came from within it
    if (!triggerInMenuDrawer) {
      var menuDetails = document.querySelectorAll('header-drawer details[open]');
      menuDetails.forEach(function (d) { d.removeAttribute('open'); });
      document.body.className = document.body.className.replace(/overflow-hidden[^ ]*/g, '').trim();
    }
  }

  disconnectedCallback() {
    if (this._onTriggerClick) {
      document.removeEventListener('click', this._onTriggerClick, true);
      this._onTriggerClick = null;
    }
    if (this._onTriggerHover) {
      document.removeEventListener('mouseenter', this._onTriggerHover, true);
      document.removeEventListener('touchstart', this._onTriggerHover, true);
      this._onTriggerHover = null;
    }
    if (this._onLoginUrlClick) {
      document.removeEventListener('click', this._onLoginUrlClick, true);
      this._onLoginUrlClick = null;
    }
    window.removeEventListener('message', this._onMessage);
    clearTimeout(this._timer);
  }
}

customElements.define('registration-overlay', RegistrationOverlay);
